import React, { useState, useEffect, useRef, useCallback, useReducer, useMemo } from "react";
import { createPortal } from "react-dom";
import { Download, RefreshCw, Lock, Crown, Users, Activity, ChevronRight, X, Plus, Trash2, Filter, Search, Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, PaintBucket, Type } from "lucide-react";
import { AV_COLORS } from "./Groups";
import { useUserProfile } from "../../context/UserProfileContext";
import axiosInstance from "../../API/interceptor";
import ExcelJS from "exceljs";

/* ══ CONSTANTS ══════════════════════════════════════════════ */
const ROW_COLORS = {
  red:"#fecaca",green:"#bbf7d0",blue:"#bae6fd",yellow:"#fef08a",
  purple:"#e9d5ff",orange:"#fed7aa",pink:"#fbcfe8",teal:"#99f6e4",
  indigo:"#c7d2fe",brown:"#d6b896",gray:"#d1d5db",cyan:"#a5f3fc",
};
const TEXT_COLORS_HEX = {
  black:"#212529",white:"#fff",red:"#dc2626",green:"#16a34a",
  blue:"#2563eb",yellow:"#ca8a04",purple:"#7c3aed",orange:"#ea580c",
  pink:"#db2777",teal:"#0d9488",indigo:"#4f46e5",gray:"#6b7280",
};
const COLOR_PALETTE    = Object.keys(ROW_COLORS);
const TEXT_COLOR_NAMES = Object.keys(TEXT_COLORS_HEX);

const FONT_FAMILIES = ["Default","Arial","Calibri","Times New Roman","Courier New","Georgia","Verdana","Trebuchet MS"];
const FONT_SIZES    = [8,9,10,11,12,14,16,18,20,24,28,36,48,72];

const ACTION_LABELS = {
  group_created     :"created the group",
  group_updated     :"updated the group",
  task_created      :"added a row",
  task_cell_updated :"updated a cell",
  task_completed    :"marked a row as done",
  task_undo         :"reopened a row",
  task_deleted      :"deleted a row",
  column_added      :"added a column",
  column_deleted    :"deleted a column",
  column_renamed    :"renamed a column",
  column_reordered  :"reordered columns",
  admin_transferred :"transferred admin access",
  member_left       :"left the group",
  permission_changed:"changed write permission",
  data_exported     :"exported the spreadsheet",
};

/* ══ PHOTO AVATAR ════════════════════════════════════════════ */
const _photoCache     = {};
const _photoListeners = {};

function PhotoAvatar({ name, userId, size = 26, style = {} }) {
  const initSrc = typeof _photoCache[userId] === "string" &&
    _photoCache[userId] !== "loading" && _photoCache[userId] !== "none"
    ? _photoCache[userId] : null;
  const [src, setSrc] = useState(initSrc);

  useEffect(() => {
    if(!userId) return;
    if(typeof _photoCache[userId] === "string" &&
       _photoCache[userId] !== "loading" && _photoCache[userId] !== "none") {
      setSrc(_photoCache[userId]); return;
    }
    if(!_photoListeners[userId]) _photoListeners[userId] = new Set();
    _photoListeners[userId].add(setSrc);
    if(_photoCache[userId] === "loading") return;
    _photoCache[userId] = "loading";
    axiosInstance.get(`/task_assignment/get_user_photo/${userId}`)
      .then(res => {
        const photo = res.data?.success && res.data?.photo ? res.data.photo : "none";
        _photoCache[userId] = photo;
        _photoListeners[userId]?.forEach(fn => fn(photo));
      })
      .catch(() => { _photoCache[userId] = "none"; });
    return () => _photoListeners[userId]?.delete(setSrc);
  }, [userId]);

  const ini   = (name||"?").split(" ").map(x=>x[0]).join("").toUpperCase().slice(0,2);
  const avBg  = AV_COLORS[((name||"").charCodeAt(0)||0) % AV_COLORS.length];

  if(src && src !== "loading" && src !== "none") {
    return (
      <img src={src} alt={name}
        className="rounded-full object-cover shrink-0"
        style={{ width:size, height:size, ...style }}/>
    );
  }
  return (
    <div className="rounded-full flex items-center justify-center text-white font-bold shrink-0"
      style={{ width:size, height:size, background:avBg, fontSize:size*0.36, ...style }}>
      {ini}
    </div>
  );
}

function colLetter(idx) {
  let s="", n=idx;
  do { s=String.fromCharCode(65+(n%26))+s; n=Math.floor(n/26)-1; } while(n>=0);
  return s;
}
function cellLabel(r,c){ return `${colLetter(c)}${r+1}`; }

function measureTextPx(text, fontSize) {
  if (!measureTextPx._cvs) measureTextPx._cvs = document.createElement("canvas");
  const ctx = measureTextPx._cvs.getContext("2d");
  ctx.font = `${fontSize}px Arial, sans-serif`;
  return ctx.measureText(String(text || "")).width;
}

/* ══ CONTEXT MENU ════════════════════════════════════════════ */
function ContextMenu({ items, pos, onClose }) {
  const ref        = useRef();
  const [subIdx,   setSubIdx]   = useState(-1);
  const [menuPos,  setMenuPos]  = useState({ x: pos.x, y: pos.y });
  const [subLeft,  setSubLeft]  = useState(false);
  const hideTimer  = useRef(null);

  useEffect(() => {
    const fn = e => { if(ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [onClose]);

  useEffect(() => {
    if(!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const vw   = window.innerWidth;
    const vh   = window.innerHeight;
    const x    = pos.x + rect.width  > vw ? Math.max(0, pos.x - rect.width)  : pos.x;
    const y    = pos.y + rect.height > vh ? Math.max(0, pos.y - rect.height)  : pos.y;
    setMenuPos({ x, y });
    setSubLeft(x + rect.width + 200 > vw);
  }, [pos.x, pos.y]);

  const openSub    = i  => { clearTimeout(hideTimer.current); setSubIdx(i); };
  const closeSub   = () => { hideTimer.current = setTimeout(() => setSubIdx(-1), 200); };
  const cancelClose = () => clearTimeout(hideTimer.current);

  return (
    <div ref={ref} className="fixed z-[99999] bg-white border border-[#d0d7de] rounded-md shadow-[0_8px_24px_rgba(0,0,0,0.18)] py-1 min-w-[200px] text-[13px]"
      style={{ left: menuPos.x, top: menuPos.y }}>
      {items.map((item,i) => {
        if(item.type==="sep") return <hr key={i} className="border-none border-t border-[#eaecef] my-[3px]"/>;
        if(item.type==="sub") {
          const isOpen = subIdx === i;
          return (
            <div key={i} className="relative">
              <div className={`px-4 py-[7px] cursor-pointer flex items-center gap-2 text-[#24292f] whitespace-nowrap ${isOpen ? "bg-[#f0f6ff]" : ""}`}
                onMouseEnter={() => openSub(i)}>
                <span className="w-[18px] text-center shrink-0">{item.icon}</span>
                <span className="flex-1">{item.label}</span>
                <ChevronRight size={12} className="text-[#8b949e] shrink-0"/>
              </div>
              {isOpen && (
                <div className={`absolute bg-white border border-[#d0d7de] rounded-md shadow-[0_8px_24px_rgba(0,0,0,0.15)] py-1 min-w-[190px] z-[99999] -top-1 ${subLeft ? "right-full" : "left-full"}`}
                  onMouseEnter={cancelClose}
                  onMouseLeave={closeSub}>
                  {item.children}
                </div>
              )}
            </div>
          );
        }
        return (
          <div key={i} onClick={() => { item.onClick?.(); onClose(); }}
            className={`px-4 py-[7px] cursor-pointer flex items-center gap-2 whitespace-nowrap ${item.danger ? "text-[#d33]" : "text-[#24292f]"} ${item.disabled ? "opacity-40 pointer-events-none" : "opacity-100 pointer-events-auto"}`}
            onMouseEnter={e => {
              e.currentTarget.style.background = item.danger ? "#fff5f5" : "#f0f6ff";
              clearTimeout(hideTimer.current);
              setSubIdx(-1);
            }}
            onMouseLeave={e => e.currentTarget.style.background=""}>
            <span className="w-[18px] text-center shrink-0">{item.icon}</span>
            <span>{item.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function BulkInsertMenu({ label, onInsert }) {
  const counts = [1, 10, 20, 50, 100];
  return (
    <>
      {counts.map(n => (
        <div key={n} onClick={() => onInsert(n)}
          className="px-4 py-[7px] cursor-pointer text-[13px] text-[#24292f] flex items-center gap-2 whitespace-nowrap"
          onMouseEnter={e => e.currentTarget.style.background="#f0f6ff"}
          onMouseLeave={e => e.currentTarget.style.background=""}>
          {n === 1 ? `Insert 1 ${label}` : `Insert ${n} ${label}s`}
        </div>
      ))}
    </>
  );
}

function ColorGrid({ onPick, current }) {
  return (
    <div className="grid gap-1 p-1 w-[180px] [grid-template-columns:repeat(6,1fr)]">
      <div onClick={() => onPick("none")}
        className={`w-6 h-6 rounded border-[1.5px] border-[#d0d7de] cursor-pointer flex items-center justify-center text-[11px] text-[#6c757d] ${current==="none" ? "bg-[#e2e8f0]" : "bg-white"}`}>✕</div>
      {COLOR_PALETTE.map(c => (
        <div key={c} onClick={() => onPick(c)}
          className={`w-6 h-6 rounded cursor-pointer box-border ${current===c ? "border-2 border-[#1f6feb]" : "border-[1.5px] border-transparent"}`}
          style={{ background:ROW_COLORS[c] }}/>
      ))}
    </div>
  );
}

function TextColorGrid({ onPick, current }) {
  return (
    <div className="grid gap-1 p-1 w-[180px] [grid-template-columns:repeat(6,1fr)]">
      <div onClick={() => onPick("none")}
        className="w-6 h-6 rounded border-[1.5px] border-[#d0d7de] cursor-pointer bg-white flex items-center justify-center text-[11px] text-[#6c757d]">✕</div>
      {TEXT_COLOR_NAMES.map(c => (
        <div key={c} onClick={() => onPick(c)}
          className={`w-6 h-6 rounded-full cursor-pointer box-border ${current===c ? "border-2 border-[#1f6feb]" : "border-[1.5px] border-[#e2e8f0]"}`}
          style={{ background:TEXT_COLORS_HEX[c] }}/>
      ))}
    </div>
  );
}

/* ══ TOAST ══════════════════════════════════════════════════ */
function Toast({ msg, type, onDone }) {
  useEffect(() => { const t=setTimeout(onDone,2800); return()=>clearTimeout(t); },[onDone]);
  const bg = type==="error"?"#ef4444":type==="warning"?"#f59e0b":"#10b981";
  return (
    <div className="fixed bottom-6 right-6 z-[99999] text-white rounded-xl px-[18px] py-2.5 text-[13px] font-semibold shadow-[0_4px_20px_rgba(0,0,0,0.18)] max-w-[320px]"
      style={{ background:bg }}>
      {msg}
    </div>
  );
}

/* ══ ACTIVITY LOG OFFCANVAS ══════════════════════════════════ */
function ActivityOffcanvas({ groupId, open, onClose, clientLogs = [] }) {
  const [serverLogs, setServerLogs] = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [page,       setPage]       = useState(1);
  const [hasMore,    setHasMore]    = useState(true);

  const load = useCallback(async (pg=1) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/task_assignment/todo_group/activity/${groupId}?page=${pg}`);
      if(res.data?.success) {
        const newLogs = res.data.logs || [];
        setServerLogs(pg===1 ? newLogs : prev=>[...prev,...newLogs]);
        setHasMore(newLogs.length >= 20);
      }
    } catch {}
    finally { setLoading(false); }
  },[groupId]);

  useEffect(() => { if(open) { setServerLogs([]); setPage(1); load(1); } },[open,load]);

  const loadMore = () => { const next=page+1; setPage(next); load(next); };

  const logs = [...clientLogs, ...serverLogs];

  const DOT_COLORS = { created:"#16a34a",edited:"#2563eb",deleted:"#dc2626",completed:"#7c3aed",undo:"#d97706",action:"#0891b2" };
  const dotCls = (action) => {
    if(action === "spreadsheet_action") return "action";
    if(action?.includes("creat")||action?.includes("add")) return "created";
    if(action?.includes("delet")||action?.includes("remov")) return "deleted";
    if(action?.includes("complet")) return "completed";
    if(action?.includes("undo")) return "undo";
    return "edited";
  };

  return (
    <>
      {open && <div onClick={onClose} className="fixed inset-0 bg-black/[.35] z-[1200]"/>}
      <div className={`fixed top-0 right-0 bottom-0 w-[380px] max-w-[95vw] bg-white z-[1201] flex flex-col transition-transform duration-300 ease-[cubic-bezier(.4,0,.2,1)] ${
        open ? "translate-x-0" : "translate-x-full"
      }`}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 shrink-0 bg-[linear-gradient(135deg,#010034,#3b82f6)]">
          <div className="flex items-center gap-2">
            <Activity size={16} color="#fff"/>
            <span className="text-white font-bold text-[15px]">Activity Log</span>
          </div>
          <button onClick={onClose} className="bg-white/[.12] border-none rounded-lg w-7 h-7 flex items-center justify-center cursor-pointer text-white">
            <X size={14}/>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading && logs.length===0 && (
            <div className="text-center py-10 text-[#94a3b8]">Loading...</div>
          )}
          {!loading && logs.length===0 && (
            <div className="text-center py-10 text-[#94a3b8] text-[13px]">No activity yet</div>
          )}
          {logs.map((log,i) => {
            const isAction = log.action_type === "spreadsheet_action";
            const cls   = dotCls(log.action_type);
            const color = DOT_COLORS[cls] || "#6c757d";
            const name  = log.done_by_name||"—";
            const label = isAction
              ? (log.details?.description || "performed an action")
              : (ACTION_LABELS[log.action_type] || log.action_type);
            const before = log.details?.before;
            const after  = log.details?.after;
            return (
              <div key={log._id||i} className="flex gap-2.5 pb-3 border-b border-[#f0f0f0] mb-1">
                <div className="w-1.5 h-1.5 rounded-full shrink-0 mt-3" style={{ background:color }}/>
                <div className="flex gap-2 flex-1 items-start">
                  <PhotoAvatar name={name} userId={log.done_by_id} size={30}/>
                  <div className="flex-1">
                    <div className="text-[13px] text-[#212529] leading-[1.5]">
                      {isAction
                        ? <span className="text-[#1e293b]">{label}</span>
                        : <><strong>{name}</strong><span className="text-[#6c757d]"> {label}</span></>
                      }
                      {!isAction && log.details?.column && (
                        <span className="text-[#6c757d] text-[11px]"> ({log.details.column})</span>
                      )}
                    </div>
                    {!isAction && log.description && (
                      <div style={{ fontSize:12,color:"#888",marginTop:2 }}>{log.description}</div>
                    )}
                    {before !== undefined && after !== undefined && (
                      <div className="text-[11px] mt-[3px] flex gap-1 items-center flex-wrap">
                        <span className="bg-[#fee2e2] text-[#991b1b] rounded-[3px] px-[5px] py-px font-mono max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap"
                          title={before}>"{before || "(empty)"}"</span>
                        <span className="text-[#94a3b8] text-[10px]">→</span>
                        <span className="bg-[#dcfce7] text-[#166534] rounded-[3px] px-[5px] py-px font-mono max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap"
                          title={after}>"{after || "(empty)"}"</span>
                      </div>
                    )}
                    <div className="text-[11px] text-[#adb5bd] mt-0.5">{log.timestamp}</div>
                  </div>
                </div>
              </div>
            );
          })}
          {hasMore && !loading && logs.length>0 && (
            <button onClick={loadMore}
              className="w-full py-2 border border-[#e2e8f0] rounded-lg bg-[#f8fafc] text-xs cursor-pointer text-[#64748b] font-semibold mt-2">
              Load More
            </button>
          )}
        </div>
      </div>
    </>
  );
}

/* ══ MEMBERS PERMISSIONS OFFCANVAS ══════════════════════════ */
function MembersOffcanvas({ groupId, open, onClose, members, isAdmin, adminId, onReload }) {
  const [localMembers, setLocalMembers] = useState(members);
  const [toast, setToast] = useState(null);
  useEffect(() => setLocalMembers(members), [members]);

  const showToast = (msg,type="success") => setToast({msg,type});

  const toggleWrite = async (memberId, currentVal) => {
    const newVal = !currentVal;
    setLocalMembers(prev => prev.map(m => m.id===memberId ? {...m,can_write:newVal} : m));
    try {
      const fd = new FormData();
      fd.append("group_id", groupId);
      fd.append("member_id", memberId);
      fd.append("write", newVal?"1":"0");
      const res = await axiosInstance.post("/task_assignment/todo_group/permission/update", fd, {
        headers:{"Content-Type":"multipart/form-data"}
      });
      if(res.data?.success) {
        const name = localMembers.find(m=>m.id===memberId)?.name||"Member";
        showToast(newVal?`Write granted to ${name} ✅`:`Write revoked for ${name} 🔒`);
      } else {
        setLocalMembers(prev => prev.map(m => m.id===memberId ? {...m,can_write:currentVal} : m));
        showToast(res.data?.error||"Permission update failed","error");
      }
    } catch {
      setLocalMembers(prev => prev.map(m => m.id===memberId ? {...m,can_write:currentVal} : m));
      showToast("Server error","error");
    }
  };

  const transferAdmin = async (memberId, memberName) => {
    if(!window.confirm(`Transfer admin role to ${memberName}? You will become a regular member.`)) return;
    try {
      const fd = new FormData();
      fd.append("group_id", groupId);
      fd.append("new_admin_id", memberId);
      const res = await axiosInstance.post("/task_assignment/todo_group/transfer_admin", fd, {
        headers:{"Content-Type":"multipart/form-data"}
      });
      if(res.data?.success) {
        showToast(`👑 Admin transferred to ${res.data.new_admin_name||memberName}`);
        setTimeout(() => { onClose(); onReload(); }, 1500);
      } else {
        showToast(res.data?.error||"Transfer failed","error");
      }
    } catch { showToast("Server error","error"); }
  };

  const leaveGroup = async () => {
    if(!window.confirm("Leave this group?")) return;
    try {
      const fd = new FormData();
      fd.append("group_id", groupId);
      const res = await axiosInstance.post("/task_assignment/todo_group/leave", fd, {
        headers:{"Content-Type":"multipart/form-data"}
      });
      if(res.data?.success) { onClose(); onReload(); }
      else showToast(res.data?.error||"Failed to leave","error");
    } catch { showToast("Server error","error"); }
  };

  return (
    <>
      {open && <div onClick={onClose} className="fixed inset-0 bg-black/[.35] z-[1200]"/>}
      <div className={`fixed top-0 right-0 bottom-0 w-[400px] max-w-[95vw] bg-white z-[1201] flex flex-col transition-transform duration-300 ease-[cubic-bezier(.4,0,.2,1)] ${
        open ? "translate-x-0" : "translate-x-full"
      }`}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 shrink-0 bg-[linear-gradient(135deg,#010034,#3b82f6)]">
          <div className="flex items-center gap-2">
            <Users size={16} color="#fff"/>
            <span className="text-white font-bold text-[15px]">Members & Permissions</span>
          </div>
          <button onClick={onClose} className="bg-white/[.12] border-none rounded-lg w-7 h-7 cursor-pointer text-white flex items-center justify-center">
            <X size={14}/>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isAdmin && (
            <div className="bg-[#eff6ff] border border-[#bfdbfe] rounded-lg px-3.5 py-2.5 text-xs text-[#1e40af] mb-3.5">
              <strong>Admin Controls:</strong> Use the toggle to grant/revoke write access. Use "Make Admin" to transfer the admin role.
            </div>
          )}
          {localMembers.map((m,i) => {
            const isThisAdmin = m.id===adminId || m.is_admin;
            return (
              <div key={m.id||i} className="flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] mb-2 bg-[#f8fafc] border border-[#e9ecef]">
                <PhotoAvatar name={m.name||""} userId={m.id} size={36}/>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[13px] font-semibold text-[#212529]">{m.name}</span>
                    {isThisAdmin && (
                      <span className="text-[10px] bg-[#fef9c3] text-[#854d0e] border border-[#fde68a] rounded px-1.5 py-px font-bold">
                        👑 ADMIN
                      </span>
                    )}
                    {!isThisAdmin && m.can_write && (
                      <span className="text-[10px] bg-[#dcfce7] text-[#166534] border border-[#bbf7d0] rounded px-1.5 py-px font-bold">
                        ✏️ Write
                      </span>
                    )}
                    {!isThisAdmin && !m.can_write && (
                      <span className="text-[10px] bg-[#fee2e2] text-[#991b1b] border border-[#fecaca] rounded px-1.5 py-px font-bold">
                        🔒 Read Only
                      </span>
                    )}
                  </div>
                </div>
                {isAdmin && !isThisAdmin && (
                  <label className="relative w-10 h-5 inline-block shrink-0 cursor-pointer">
                    <input type="checkbox" checked={!!m.can_write}
                      onChange={() => toggleWrite(m.id, !!m.can_write)}
                      className="opacity-0 w-0 h-0"/>
                    <span className="absolute cursor-pointer inset-0 rounded-[20px] transition-[0.3s]" style={{ background:m.can_write?"#16a34a":"#ccc" }}>
                      <span className="absolute h-3.5 w-3.5 bottom-[3px] bg-white rounded-full transition-[0.3s]" style={{ left:m.can_write?22:3 }}/>
                    </span>
                  </label>
                )}
                {isAdmin && !isThisAdmin && (
                  <button onClick={() => transferAdmin(m.id, m.name)}
                    className="px-2.5 py-[3px] border border-[#f59e0b] rounded-md text-[11px] font-semibold cursor-pointer bg-white text-[#92400e] whitespace-nowrap">
                    👑 Make Admin
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[#f3f4f6] bg-[#f9fafb] flex justify-between items-center shrink-0">
          {isAdmin
            ? <span className="text-[11px] text-[#6c757d]">Transfer admin role before leaving.</span>
            : <button onClick={leaveGroup}
                className="px-3.5 py-1.5 border border-[#ef4444] rounded-lg text-xs font-semibold cursor-pointer bg-white text-[#ef4444]">
                🚪 Leave Group
              </button>
          }
          <button onClick={onClose}
            className="px-4 py-1.5 border-none rounded-lg text-white text-xs font-semibold cursor-pointer bg-[linear-gradient(135deg,#010034,#3b82f6)]">
            Done
          </button>
        </div>
      </div>
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={()=>setToast(null)}/>}
    </>
  );
}

/* ══ FILTER HELPERS ═════════════════════════════════════════ */
function parseFlexDate(str) {
  if (!str) return null;
  const m1 = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (m1) return new Date(+m1[3], +m1[2]-1, +m1[1], +(m1[4]||0), +(m1[5]||0), +(m1[6]||0)).getTime();
  const m2 = str.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (m2) return new Date(+m2[1], +m2[2]-1, +m2[3], +(m2[4]||0), +(m2[5]||0), +(m2[6]||0)).getTime();
  const t = Date.parse(str);
  return isNaN(t) ? null : t;
}

function detectColType(vals) {
  const nb = vals.filter(v => v !== "" && v != null);
  if (nb.length === 0) return "text";
  const isDate = v => /^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4}/.test(v) || /^\d{4}[\/\-]\d{1,2}[\/\-]\d{1,2}/.test(v);
  const isNum  = v => v.trim() !== "" && !isNaN(Number(v));
  if (nb.filter(isDate).length / nb.length >= 0.7) return "date";
  if (nb.filter(isNum).length  / nb.length >= 0.8) return "number";
  return "text";
}

const SORT_LABELS = {
  text:   [["asc","↑  Sort A to Z"],          ["desc","↓  Sort Z to A"]],
  number: [["asc","↑  Smallest to Largest"],   ["desc","↓  Largest to Smallest"]],
  date:   [["asc","↑  Sort Oldest to Newest"], ["desc","↓  Sort Newest to Oldest"]],
};

/* ══ FILTER DROPDOWN ════════════════════════════════════════ */
function FilterDropdown({ ci, uniqueVals, activeFilter, activeSort, pos: initPos, btnEl, onApply, onClose }) {
  const [search,  setSearch]  = useState("");
  const [checked, setChecked] = useState(() =>
    activeFilter ? new Set(activeFilter) : new Set(uniqueVals.map(String))
  );
  const sortDir = activeSort || null;
  const ref = useRef();

  const colType    = useMemo(() => detectColType(uniqueVals), [uniqueVals]);
  const sortLabels = SORT_LABELS[colType] || SORT_LABELS.text;

  useEffect(() => {
    if (!btnEl || !ref.current) return;
    const update = () => {
      if (!ref.current) return;
      const r = btnEl.getBoundingClientRect();
      ref.current.style.left = `${r.left}px`;
      ref.current.style.top  = `${r.bottom + 2}px`;
    };
    const scrollEls = [];
    let el = btnEl.parentElement;
    while (el && el !== document.body) {
      const { overflow, overflowY } = window.getComputedStyle(el);
      if (/auto|scroll/.test(overflow + overflowY)) {
        scrollEls.push(el);
        el.addEventListener("scroll", update, { passive: true });
      }
      el = el.parentElement;
    }
    window.addEventListener("scroll", update, { passive: true });
    return () => {
      scrollEls.forEach(e => e.removeEventListener("scroll", update));
      window.removeEventListener("scroll", update);
    };
  }, [btnEl]);

  useEffect(() => {
    const fn = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const t = setTimeout(() => document.addEventListener("mousedown", fn), 0);
    return () => { clearTimeout(t); document.removeEventListener("mousedown", fn); };
  }, [onClose]);

  const displayed  = uniqueVals.filter(v => String(v).toLowerCase().includes(search.toLowerCase()));
  const allChecked = displayed.length > 0 && displayed.every(v => checked.has(String(v)));
  const someChecked = displayed.some(v => checked.has(String(v)));

  const toggleVal = v => setChecked(prev => {
    const s = new Set(prev);
    if (s.has(String(v))) s.delete(String(v)); else s.add(String(v));
    return s;
  });

  const toggleAll = () => {
    if (allChecked) setChecked(prev => { const s = new Set(prev); displayed.forEach(v => s.delete(String(v))); return s; });
    else            setChecked(prev => { const s = new Set(prev); displayed.forEach(v => s.add(String(v)));    return s; });
  };

  const BG = "#ffffff", BORDER = "#e2e8f0", HOVER = "#f1f5f9", TEXT = "#1e293b";
  const ACCENT = "#7C3AED", ACCENT_LIGHT = "#f3f0ff";

  return (
    <div ref={ref}
      onMouseDown={e => e.stopPropagation()}
      className="fixed z-[99999] rounded-lg shadow-[0_8px_32px_rgba(0,0,0,0.13)] min-w-[220px] max-w-[280px] text-xs select-none border"
      style={{ left:initPos.x, top:initPos.y, willChange:"transform", background:BG, borderColor:BORDER, color:TEXT }}>

      {/* Sort buttons */}
      <div className="border-b py-1" style={{ borderColor:BORDER }}>
        {sortLabels.map(([dir, label]) => (
          <div key={dir}
            onClick={() => {
              const newDir = sortDir === dir ? null : dir;
              onApply({ filter: checked, sort: newDir }, ci);
            }}
            className="px-3.5 py-[7px] cursor-pointer flex items-center gap-2 rounded mx-1"
            style={{
              background: sortDir === dir ? ACCENT_LIGHT : "transparent",
              color: sortDir === dir ? ACCENT : TEXT,
              fontWeight: sortDir === dir ? 600 : 400,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = sortDir===dir ? ACCENT_LIGHT : HOVER; }}
            onMouseLeave={e => { e.currentTarget.style.background = sortDir===dir ? ACCENT_LIGHT : "transparent"; }}>
            {sortDir === dir && <span className="font-black text-[10px]" style={{ color:ACCENT }}>✓ </span>}
            {label}
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="px-2 pt-2 pb-1.5 border-b" style={{ borderColor:BORDER }}>
        <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
          placeholder="🔍  Search values..."
          className="w-full bg-[#f8fafc] rounded-md px-2.5 py-[5px] text-xs outline-none box-border transition-colors border"
          style={{ color:TEXT, borderColor:BORDER }}
          onFocus={e => e.target.style.borderColor = ACCENT}
          onBlur={e => e.target.style.borderColor = BORDER} />
      </div>

      {/* Checkbox list */}
      <div className="max-h-[200px] overflow-y-auto py-1">
        <label className="flex items-center gap-2 px-3.5 py-1.5 cursor-pointer rounded mx-1"
          onMouseEnter={e => e.currentTarget.style.background = HOVER}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
          <input type="checkbox" checked={allChecked}
            ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
            onChange={toggleAll} className="cursor-pointer w-3.5 h-3.5" style={{ accentColor:ACCENT }} />
          <span className="font-bold text-[#374151]">(Select All)</span>
        </label>
        {displayed.map((v, i) => (
          <label key={i} className="flex items-center gap-2 px-3.5 py-1.5 cursor-pointer rounded mx-1"
            onMouseEnter={e => e.currentTarget.style.background = HOVER}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <input type="checkbox" checked={checked.has(String(v))} onChange={() => toggleVal(v)}
              className="cursor-pointer w-3.5 h-3.5" style={{ accentColor:ACCENT }} />
            <span className="overflow-hidden text-ellipsis whitespace-nowrap" style={{ color:TEXT }}>
              {String(v) === "" ? <em className="text-[#94a3b8]">(Blank)</em> : String(v)}
            </span>
          </label>
        ))}
        {displayed.length === 0 && (
          <div className="px-3.5 py-3 text-[#94a3b8] text-center">No results</div>
        )}
      </div>

      {/* OK / Cancel */}
      <div className="flex gap-2 px-2.5 py-2 justify-end bg-[#f8fafc] rounded-b-lg border-t" style={{ borderColor:BORDER }}>
        <button onClick={onClose}
          className="px-4 py-[5px] rounded-md bg-white text-[#64748b] cursor-pointer text-xs font-medium border" style={{ borderColor:BORDER }}>
          Cancel
        </button>
        <button onClick={() => onApply({ filter: checked, sort: sortDir }, ci)}
          className="px-4 py-[5px] rounded-md border-none text-white cursor-pointer text-xs font-bold shadow-[0_2px_6px_rgba(124,58,237,0.3)]"
          style={{ background:ACCENT }}>
          OK
        </button>
      </div>
    </div>
  );
}

/* ══ FIND & REPLACE BAR ═════════════════════════════════════ */
function FindBar({ findVal, replaceVal, matchCase, matchResults, matchIdx,
  onFindChange, onReplaceChange, onMatchCaseToggle, onNext, onPrev,
  onReplace, onReplaceAll, onClose, canWrite }) {
  const findRef = useRef();
  useEffect(() => { findRef.current?.focus(); }, []);

  const count   = matchResults.length;
  const current = count > 0 ? matchIdx + 1 : 0;
  const noMatch = findVal && count === 0;

  const btnCls = "flex items-center justify-center border border-[#d0d7de] rounded cursor-pointer bg-[#f6f8fa] text-[11px] font-semibold px-2.5 text-[#374151] h-[26px] whitespace-nowrap";

  return (
    <div className="fixed top-20 right-6 z-[9998] bg-white border border-[#e2e8f0] rounded-[10px] shadow-[0_12px_36px_rgba(0,0,0,0.18)] p-3.5 w-[350px] text-xs flex flex-col gap-2.5">

      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="font-bold text-[13px] text-[#1f2937]">🔍 Find &amp; Replace</span>
        <button onClick={onClose}
          className="bg-transparent border-none cursor-pointer text-lg text-[#9ca3af] leading-none">×</button>
      </div>

      {/* Find row */}
      <div className="flex items-center gap-[5px]">
        <div className="flex-1 relative">
          <input
            ref={findRef}
            value={findVal}
            onChange={(e) => onFindChange(e.target.value)}
            placeholder="Find in spreadsheet..."
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.shiftKey ? onPrev() : onNext();
              }
              if (e.key === "Escape") {
                onClose();
              }
            }}
            className={`xl-find-input w-full border rounded-[5px] px-2 py-[5px] text-xs outline-none box-border transition-colors ${
              noMatch
                ? "border-[#f87171] bg-[#fff5f5]"
                : "border-[#d0d7de] bg-white"
            }`}
          />
        </div>
        <span className={`text-[10px] min-w-[48px] text-center shrink-0 ${noMatch ? "text-[#ef4444] font-bold" : "text-[#6b7280] font-normal"}`}>
          {findVal ? (noMatch ? "No match" : `${current} / ${count}`) : ""}
        </span>
        <button onClick={onPrev} disabled={count===0}
          title="Previous match (Shift+Enter)"
          className={btnCls} style={{ opacity:count===0?0.4:1 }}>↑</button>
        <button onClick={onNext} disabled={count===0}
          title="Next match (Enter)"
          className={btnCls} style={{ opacity:count===0?0.4:1 }}>↓</button>
      </div>

      {/* Replace row — hidden in read-only mode */}
      {canWrite ? (
        <div className="flex items-center gap-[5px]">
          <input value={replaceVal} onChange={e => onReplaceChange(e.target.value)}
            placeholder="Replace with..."
            className="xl-find-input flex-1 border border-[#d0d7de] rounded-[5px] px-2 py-[5px] text-xs outline-none"
            onKeyDown={e => { if(e.key==="Escape") onClose(); }} />
          <button onClick={onReplace} disabled={count===0}
            className={btnCls} style={{ opacity:count===0?0.4:1 }}>Replace</button>
          <button onClick={onReplaceAll} disabled={count===0}
            className={`${btnCls} bg-[#7C3AED] text-white border-none`} style={{ opacity:count===0?0.4:1 }}>
            Replace All
          </button>
        </div>
      ) : (
        <div className="text-[11px] text-[#92400e] bg-[#fef3c7] border border-[#fde68a] rounded-[5px] px-2 py-[5px] flex items-center gap-[5px]">
          🔒 Replace is disabled — you have read-only access.
        </div>
      )}

      {/* Options row */}
      <div className="flex items-center gap-2.5 pt-1.5 border-t border-[#f1f5f9]">
        <label className="flex items-center gap-[5px] cursor-pointer text-[11px] text-[#374151] select-none">
          <input type="checkbox" checked={matchCase} onChange={onMatchCaseToggle}
            className="cursor-pointer" style={{ accentColor:"#7C3AED" }} />
          Match Case
        </label>
        {count > 0 && (
          <span className="text-[10px] text-[#6b7280] ml-auto">
            {count} match{count!==1?"es":""} found
          </span>
        )}
      </div>
    </div>
  );
}

/* ── Optimistic row/col shift helpers ── */
function shiftMapIns(m, at)  { const n={}; Object.entries(m).forEach(([k,v])=>{ const r=+k; n[r<at?k:r+1]=v; }); return n; }
function shiftMapDel(m, del) { const n={}; Object.entries(m).forEach(([k,v])=>{ const r=+k; if(r<del) n[k]=v; else if(r>del) n[r-1]=v; }); return n; }
function shiftCellIns(m, at)  { const n={}; Object.entries(m).forEach(([k,v])=>{ const [rs,cs]=k.split('-'); const r=+rs; n[r<at?k:`${r+1}-${cs}`]=v; }); return n; }
function shiftCellDel(m, del) { const n={}; Object.entries(m).forEach(([k,v])=>{ const [rs,cs]=k.split('-'); const r=+rs; if(r<del) n[k]=v; else if(r>del) n[`${r-1}-${cs}`]=v; }); return n; }
function shiftCellColDel(m, dc){ const n={}; Object.entries(m).forEach(([k,v])=>{ const [rs,cs]=k.split('-'); const c=+cs; if(c<dc) n[k]=v; else if(c>dc) n[`${rs}-${c-1}`]=v; }); return n; }
function removeColGrid(g, ci)  { const n={}; Object.entries(g).forEach(([r,row])=>{ n[r]={}; Object.entries(row).forEach(([c,v])=>{ const cc=+c; if(cc<ci) n[r][cc]=v; else if(cc>ci) n[r][cc-1]=v; }); }); return n; }
function insertColGrid(g, ci)  { const n={}; Object.entries(g).forEach(([r,row])=>{ n[r]={}; Object.entries(row).forEach(([c,v])=>{ const cc=+c; n[r][cc<ci?cc:cc+1]=v; }); n[r][ci]=""; }); return n; }
function insertRowGrid(g, at, nc) { const n={}; Object.entries(g).forEach(([r,row])=>{ const ri=+r; n[ri<at?r:ri+1]=row; }); n[at]=Object.fromEntries(Array.from({length:nc},(_,ci)=>[ci,""])); return n; }
function deleteRowGrid(g, del)    { const n={}; Object.entries(g).forEach(([r,row])=>{ const ri=+r; if(ri<del) n[r]=row; else if(ri>del) n[ri-1]=row; }); return n; }

const EMPTY_ARR = [];
const EMPTY_OBJ = {};

// Rows/columns waiting for the server: red pulse = being deleted, green pulse = being added.
// Animations override the cells' inline backgrounds; reduced-motion users get a steady tint.
const SHEET_PENDING_CSS = `
@keyframes sheetPulseDel { 0%,100% { background-color:#fee2e2; } 50% { background-color:#fca5a5; } }
@keyframes sheetPulseAdd { 0%,100% { background-color:#dcfce7; } 50% { background-color:#86efac; } }
@keyframes sheetProgress { 0% { transform:translateX(-100%); } 100% { transform:translateX(400%); } }
.sheet-pending-del { animation: sheetPulseDel 1.1s ease-in-out infinite; color:#991b1b !important;
  text-decoration: line-through; opacity:.75; pointer-events:none; }
.sheet-pending-add { animation: sheetPulseAdd 1.1s ease-in-out infinite; pointer-events:none; }
.sheet-progress-bar { animation: sheetProgress 1.2s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) {
  .sheet-pending-del { animation:none; background-color:#fee2e2 !important; }
  .sheet-pending-add { animation:none; background-color:#dcfce7 !important; }
  .sheet-progress-bar { animation:none; width:100% !important; opacity:.6; }
}
`;

/* ══ SPREADSHEET ════════════════════════════════════════════ */
function Spreadsheet({ groupId, groupName, groupColor, canWrite, isAdmin, columns, colDisplayNames, colStyles,
  headerRow, tasks: initTasks, members, adminId, onReload, onMembershipChange }) {

  // ── State ──
  const [tasks,    setTasks]    = useState(initTasks);
  const [cols,     setCols]     = useState(columns);
  const [colW,     setColW]     = useState(() => columns.map(()=>140));
  const [rowH,     setRowH]     = useState({});

  const buildGrid = useCallback((tks, cs) => {
    const g = {};
    g[0] = {};
    const hrd = headerRow?.row_data || {};
    cs.forEach((col,ci) => { g[0][ci] = hrd[col] ?? colDisplayNames[col] ?? col; });
    tks.forEach((t,ri) => {
      const rd = t.row_data || {};
      g[ri+1] = {};
      cs.forEach((col,ci) => { g[ri+1][ci] = rd[col] ?? ""; });
    });
    return g;
  }, [headerRow, colDisplayNames]);

  const [grid,     setGrid]     = useState(() => buildGrid(initTasks, columns));

  useEffect(() => {
    setTasks(initTasks);
    setCols(columns);
    setColW(columns.map(()=>140));
    setGrid(buildGrid(initTasks, columns));
  }, [initTasks, columns, buildGrid]);

  // ── Selection ──
  const [selStart,    setSelStart]    = useState(null);
  const [selEnd,      setSelEnd]      = useState(null);
  const [activeCell,  setActiveCell]  = useState(null);
  const [editCell,    setEditCell]    = useState(null);
  const [editVal,     setEditVal]     = useState("");
  const [editOrig,    setEditOrig]    = useState("");
  const isDragging    = useRef(false);
  const inputRef      = useRef();

  const [copyRange,   setCopyRange]   = useState(null);
  const [isCut,       setIsCut]       = useState(false);

  const [rowBg,       setRowBg]       = useState(() => {
    const m={}; initTasks.forEach((t,i)=>{ if(t.row_color) m[i+1]=t.row_color; }); return m;
  });
  const [rowTx,       setRowTx]       = useState(() => {
    const m={}; initTasks.forEach((t,i)=>{ if(t.row_text_color) m[i+1]=t.row_text_color; }); return m;
  });
  const [cellBg,      setCellBg]      = useState(() => {
    const m={}; initTasks.forEach((t,i)=>{
      Object.entries(t.cell_styles||{}).forEach(([colKey,style])=>{
        const ci = columns.indexOf(colKey);
        if(ci!==-1 && style?.bg) m[`${i+1}-${ci}`]=style.bg;
      });
    }); return m;
  });
  const [cellTx,      setCellTx]      = useState(() => {
    const m={}; initTasks.forEach((t,i)=>{
      Object.entries(t.cell_styles||{}).forEach(([colKey,style])=>{
        const ci = columns.indexOf(colKey);
        if(ci!==-1 && style?.text) m[`${i+1}-${ci}`]=style.text;
      });
    }); return m;
  });
  const [rowComplete, setRowComplete] = useState(() => {
    const m={}; initTasks.forEach((t,i)=>{ if(t.status==="complete") m[i+1]=true; }); return m;
  });
  const [colBg,       setColBg]       = useState(() => {
    const m={}; Object.entries(colStyles).forEach(([k,v])=>{ if(v.bg) m[k]=v.bg; }); return m;
  });
  const [colTx,       setColTx]       = useState(() => {
    const m={}; Object.entries(colStyles).forEach(([k,v])=>{ if(v.text) m[k]=v.text; }); return m;
  });

  const [history,  setHistory]  = useState([]);
  const [future,   setFuture]   = useState([]);

  const pushHistory = useCallback((prevGrid, rowSnap=null) => {
    setHistory(h => [...h.slice(-29), {
      grid:        JSON.parse(JSON.stringify(prevGrid)),
      mergedCells: JSON.parse(JSON.stringify(mergedCellsRef.current)),
      cellFormat:  JSON.parse(JSON.stringify(cellFormatRef.current)),
      ...(rowSnap ? { rowSnap } : {}),
    }]);
    setFuture([]);
  }, []);

  const [saving,        setSaving]        = useState(false);
  const [toast,         setToast]         = useState(null);
  const [ctx,           setCtx]           = useState(null);
  const [activityOpen,  setActivityOpen]  = useState(false);
  const [membersOpen,   setMembersOpen]   = useState(false);
  const [shortcutOpen,  setShortcutOpen]  = useState(false);
  const [exportOpen,    setExportOpen]    = useState(false);
  const exportRef = useRef();

  const [findOpen,    setFindOpen]    = useState(false);
  const [findVal,     setFindVal]     = useState("");
  const [replaceVal,  setReplaceVal]  = useState("");
  const [matchCase,   setMatchCase]   = useState(false);
  const [matchIdx,    setMatchIdx]    = useState(0);

  const [autoSumOpen,   setAutoSumOpen]   = useState(false);
  const [autoSumResult, setAutoSumResult] = useState(null);
  const [mergedCells,   setMergedCells]   = useState([]);
  const [mergeOpen,     setMergeOpen]     = useState(false);
  const [cellFormat,    setCellFormat]    = useState({});
  const [fillColorOpen, setFillColorOpen] = useState(false);
  const [fontColorOpen, setFontColorOpen] = useState(false);
  const [fillColorPos,  setFillColorPos]  = useState({ top:0, left:0 });
  const [fontColorPos,  setFontColorPos]  = useState({ top:0, left:0 });

  const [freezeTopRow,   setFreezeTopRow]   = useState(false);
  const [freezeFirstCol, setFreezeFirstCol] = useState(false);
  const [freezeDropOpen, setFreezeDropOpen] = useState(false);
  const freezeDropRef = useRef();

  const [removeDupStep,        setRemoveDupStep]        = useState(0);
  const [removeDupWarn,        setRemoveDupWarn]        = useState("expand");
  const [removeDupCols,        setRemoveDupCols]        = useState([]);
  const [removeDupDisplayCols, setRemoveDupDisplayCols] = useState([]);
  const [removeDupHeaders,     setRemoveDupHeaders]     = useState(true);
  const [removeDupResult,      setRemoveDupResult]      = useState(null);

  const { profile } = useUserProfile();
  const myName = profile?.alias_name || `${profile?.firstname||""} ${profile?.lastname||""}`.trim() || "Someone";
  const [clientLogs, setClientLogs] = useState([]);

  const [autoFillDragging, setAutoFillDragging] = useState(false);
  const [autoFillEnd,      setAutoFillEnd]      = useState(null);
  const autoFillDragRef = useRef(false);
  const autoFillEndRef  = useRef(null);

  const wrapRef          = useRef();
  const tableRef         = useRef();
  const autoSumRef       = useRef();
  const mergeRef         = useRef();
  const fillColorRef     = useRef();
  const fontColorRef     = useRef();
  const mergedCellsRef   = useRef([]);
  const cellFormatRef    = useRef({});
  useEffect(() => { mergedCellsRef.current = mergedCells; }, [mergedCells]);
  useEffect(() => { cellFormatRef.current  = cellFormat;  }, [cellFormat]);

  const [colFilters,   setColFilters]   = useState({});
  const [colSortDir,   setColSortDir]   = useState({});
  const [filterDrop,   setFilterDrop]   = useState(null);

  const showToast = useCallback((msg,type="success") => setToast({msg,type}), []);
  const numRows   = tasks.length+1;
  const gutterW   = Math.max(36, String(numRows).length * 8 + 20);
  const numCols   = cols.length;

  const logActivity = useCallback((description, details = {}) => {
    const ts = new Date().toLocaleString("en-US", {
      year:"numeric", month:"short", day:"numeric",
      hour:"2-digit", minute:"2-digit",
    });
    const entry = {
      _id: `cl-${Date.now()}-${Math.random()}`,
      action_type: "spreadsheet_action",
      done_by_name: myName,
      done_by_id: profile?.user_id,
      timestamp: ts,
      details: { description, ...details },
    };
    setClientLogs(prev => [entry, ...prev.slice(0, 99)]);
    axiosInstance.post("/task_assignment/todo_group/log_action", {
      group_id: groupId, description, ...details,
    }).catch(() => {});
  }, [groupId, myName, profile]);

  const selLabel = useCallback((ss = selStart, se = selEnd, ac = activeCell) => {
    if(!ss || !se) return ac ? cellLabel(ac.r, ac.c) : "";
    if(ss.r === se.r && ss.c === se.c) return cellLabel(ss.r, ss.c);
    return `${cellLabel(ss.r, ss.c)}–${cellLabel(se.r, se.c)}`;
  }, [selStart, selEnd, activeCell]);

  const visibleDataRows = useMemo(() => {
    let rows = Array.from({length: tasks.length}, (_, i) => i + 1);
    const sortKey = Object.keys(colSortDir).find(k => colSortDir[k]);
    if (sortKey) {
      const ci = Number(sortKey), dir = colSortDir[sortKey];
      rows.sort((a, b) => {
        const va = String(grid[a]?.[ci] ?? ""), vb = String(grid[b]?.[ci] ?? "");
        if (va === "" && vb === "") return 0;
        if (va === "") return 1;
        if (vb === "") return -1;
        const da = parseFlexDate(va), db = parseFlexDate(vb);
        if (da !== null && db !== null) return dir === "asc" ? da - db : db - da;
        const na = Number(va), nb = Number(vb);
        if (!isNaN(na) && !isNaN(nb)) return dir === "asc" ? na - nb : nb - na;
        const cmp = va.localeCompare(vb, undefined, { numeric: true, sensitivity: "base" });
        return dir === "asc" ? cmp : -cmp;
      });
    }
    Object.keys(colFilters).forEach(k => {
      const set = colFilters[k]; if (!set || set.size === 0) return;
      const ci = Number(k);
      rows = rows.filter(ri => set.has(String(grid[ri]?.[ci] ?? "")));
    });
    return rows;
  }, [tasks.length, colFilters, colSortDir, grid]);

  const hasActiveFilter = Object.keys(colFilters).length > 0 || Object.keys(colSortDir).length > 0;

  const activeFmt = useMemo(() =>
    activeCell ? (cellFormat[`${activeCell.r}-${activeCell.c}`] || {}) : {},
  [activeCell, cellFormat]);

  const mergeCoveredSet = useMemo(() => {
    const set = new Set();
    mergedCells.forEach(m => {
      for (let r = m.r1; r <= m.r2; r++) {
        for (let c = m.c1; c <= m.c2; c++) {
          if (!(r === m.r1 && c === m.c1)) set.add(`${r}-${c}`);
        }
      }
    });
    return set;
  }, [mergedCells]);

  const mergeOriginMap = useMemo(() => {
    const map = {};
    mergedCells.forEach(m => { map[`${m.r1}-${m.c1}`] = m; });
    return map;
  }, [mergedCells]);

  const applyFilter = useCallback(({ filter, sort }, ci) => {
    const allVals = new Set(Array.from({length: tasks.length}, (_, i) => String(grid[i+1]?.[ci] ?? "")));
    setColFilters(prev => {
      const next = { ...prev };
      if (!filter || (filter.size >= allVals.size && [...allVals].every(v => filter.has(v)))) {
        delete next[ci];
      } else {
        next[ci] = filter;
      }
      return next;
    });
    setColSortDir(prev => {
      const next = {};
      if (sort) next[ci] = sort;
      return next;
    });
    setFilterDrop(null);
  }, [tasks.length, grid]);

  const clearAllFilters = useCallback(() => {
    setColFilters({});
    setColSortDir({});
    setFilterDrop(null);
  }, []);

  const computeAutoSum = useCallback(async (fn) => {
    if (!activeCell) { showToast("Select a cell first", "warning"); return; }
    let vals = [];

    const hasRange = selStart && selEnd &&
      (selStart.r !== selEnd.r || selStart.c !== selEnd.c);

    const targetCol = activeCell.c;
    let   rangeR2   = activeCell.r;

    if (hasRange) {
      const r1 = Math.max(1, Math.min(selStart.r, selEnd.r));
      const r2 = Math.max(selStart.r, selEnd.r);
      const c1 = Math.min(selStart.c, selEnd.c);
      const c2 = Math.max(selStart.c, selEnd.c);
      rangeR2 = r2;
      for (let r = r1; r <= r2; r++)
        for (let c = c1; c <= c2; c++) {
          const v = grid[r]?.[c] ?? "";
          if (v !== "" && !isNaN(Number(v))) vals.push(Number(v));
        }
    } else {
      const c = activeCell.c;
      for (const r of visibleDataRows) {
        const v = grid[r]?.[c] ?? "";
        if (v !== "" && !isNaN(Number(v))) vals.push(Number(v));
      }
      const lastDataRow = [...visibleDataRows].reverse().find(r => {
        const v = grid[r]?.[c] ?? "";
        return v !== "" && !isNaN(Number(v));
      });
      rangeR2 = lastDataRow ?? (visibleDataRows[visibleDataRows.length - 1] ?? activeCell.r);
    }

    let result = 0;
    if (fn === "Sum")                result = vals.reduce((a, b) => a + b, 0);
    else if (fn === "Average")       result = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    else if (fn === "Count Numbers") result = vals.length;
    else if (fn === "Max")           result = vals.length ? Math.max(...vals) : 0;
    else if (fn === "Min")           result = vals.length ? Math.min(...vals) : 0;

    const formatted = (fn !== "Count Numbers" && !Number.isInteger(result))
      ? parseFloat(result.toFixed(4)) : result;
    const resultStr = String(formatted);

    setAutoSumResult({ label: fn, value: formatted });
    setAutoSumOpen(false);
    logActivity(`${myName} applied AutoSum (${fn}) = ${formatted} in column ${colLetter(activeCell.c)}`);

    if (!canWrite) return;
    const lastCellVal = grid[rangeR2]?.[targetCol] ?? "";
    const lastCellEmpty = rangeR2 >= 1 && (lastCellVal === "" || isNaN(Number(lastCellVal)));
    const targetRow = lastCellEmpty ? rangeR2 : rangeR2 + 1;
    const task      = tasks[targetRow - 1];
    const col       = cols[targetCol];
    if (!col) return;

    pushHistory(grid);
    setGrid(prev => ({
      ...prev,
      [targetRow]: { ...(prev[targetRow] || {}), [targetCol]: resultStr }
    }));
    setSelStart({r:targetRow, c:targetCol});
    setSelEnd({r:targetRow, c:targetCol});
    setActiveCell({r:targetRow, c:targetCol});

    if (task?._id) {
      setSaving(true);
      try {
        await axiosInstance.post("/task_assignment/todo_group/task/cell_update",
          { task_id: task._id, group_id: groupId, field: col, value: resultStr });
      } catch { showToast("Failed to save result", "error"); }
      finally { setSaving(false); }
    }
  }, [activeCell, selStart, selEnd, visibleDataRows, grid, tasks, cols, groupId, canWrite, pushHistory, showToast, logActivity, myName]);

  useEffect(() => {
    if (!autoSumOpen) return;
    const handler = e => {
      if (autoSumRef.current && !autoSumRef.current.contains(e.target)) setAutoSumOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [autoSumOpen]);

  useEffect(() => {
    if (!freezeDropOpen) return;
    const handler = e => {
      if (freezeDropRef.current && !freezeDropRef.current.contains(e.target)) setFreezeDropOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [freezeDropOpen]);

  useEffect(() => {
    if (!exportOpen) return;
    const handler = e => { if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [exportOpen]);

  const doMergeCells = useCallback((centerAlign) => {
    if (!selStart || !selEnd) { showToast("Select a range first","warning"); return; }
    const r1 = Math.max(1, Math.min(selStart.r, selEnd.r));
    const r2 = Math.max(selStart.r, selEnd.r);
    const c1 = Math.min(selStart.c, selEnd.c);
    const c2 = Math.max(selStart.c, selEnd.c);
    if (r1 === r2 && c1 === c2) { showToast("Select more than one cell to merge","warning"); return; }
    pushHistory(grid);
    setMergedCells(prev => [
      ...prev.filter(m => m.r2 < r1 || m.r1 > r2 || m.c2 < c1 || m.c1 > c2),
      { r1, c1, r2, c2, center: centerAlign }
    ]);
    setMergeOpen(false);
    logActivity(`${myName} merged cells ${cellLabel(r1,c1)}–${cellLabel(r2,c2)}${centerAlign?" (centered)":""}`);
  }, [selStart, selEnd, showToast, pushHistory, grid, logActivity, myName]);

  const doMergeAcross = useCallback(() => {
    if (!selStart || !selEnd) { showToast("Select a range first","warning"); return; }
    const r1 = Math.max(1, Math.min(selStart.r, selEnd.r));
    const r2 = Math.max(selStart.r, selEnd.r);
    const c1 = Math.min(selStart.c, selEnd.c);
    const c2 = Math.max(selStart.c, selEnd.c);
    if (c1 === c2) { showToast("Select multiple columns to merge across","warning"); return; }
    const newMerges = [];
    for (let r = r1; r <= r2; r++) newMerges.push({ r1: r, c1, r2: r, c2, center: true });
    pushHistory(grid);
    setMergedCells(prev => [
      ...prev.filter(m => m.r2 < r1 || m.r1 > r2 || m.c2 < c1 || m.c1 > c2),
      ...newMerges
    ]);
    setMergeOpen(false);
  }, [selStart, selEnd, showToast, pushHistory, grid]);

  const doUnmerge = useCallback(() => {
    if (!selStart || !selEnd) return;
    const r1 = Math.min(selStart.r, selEnd.r);
    const r2 = Math.max(selStart.r, selEnd.r);
    const c1 = Math.min(selStart.c, selEnd.c);
    const c2 = Math.max(selStart.c, selEnd.c);
    pushHistory(grid);
    setMergedCells(prev => prev.filter(m =>
      m.r2 < r1 || m.r1 > r2 || m.c2 < c1 || m.c1 > c2
    ));
    setMergeOpen(false);
  }, [selStart, selEnd, pushHistory, grid]);

  useEffect(() => {
    if (!mergeOpen) return;
    const handler = e => {
      if (mergeRef.current && !mergeRef.current.contains(e.target)) setMergeOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [mergeOpen]);

  const applyFormat = useCallback((fmtUpdate) => {
    if (!activeCell) return;
    const r1 = selStart && selEnd ? Math.min(selStart.r, selEnd.r) : activeCell.r;
    const r2 = selStart && selEnd ? Math.max(selStart.r, selEnd.r) : activeCell.r;
    const c1 = selStart && selEnd ? Math.min(selStart.c, selEnd.c) : activeCell.c;
    const c2 = selStart && selEnd ? Math.max(selStart.c, selEnd.c) : activeCell.c;
    pushHistory(grid);
    setCellFormat(prev => {
      const next = { ...prev };
      for (let r = r1; r <= r2; r++) {
        for (let c = c1; c <= c2; c++) {
          const key = `${r}-${c}`;
          next[key] = { ...(next[key] || {}), ...fmtUpdate };
        }
      }
      return next;
    });

    const range = selLabel({ r:r1,c:c1 }, { r:r2,c:c2 }, activeCell);
    if(fmtUpdate.bold !== undefined)
      logActivity(`${myName} ${fmtUpdate.bold ? "applied" : "removed"} bold on ${range}`);
    else if(fmtUpdate.italic !== undefined)
      logActivity(`${myName} ${fmtUpdate.italic ? "applied" : "removed"} italic on ${range}`);
    else if(fmtUpdate.underline !== undefined)
      logActivity(`${myName} ${fmtUpdate.underline ? "applied" : "removed"} underline on ${range}`);
    else if(fmtUpdate.fontSize !== undefined)
      logActivity(`${myName} changed font size to ${fmtUpdate.fontSize}px on ${range}`);
    else if(fmtUpdate.fontFamily !== undefined)
      logActivity(`${myName} changed font to ${fmtUpdate.fontFamily || "Default"} on ${range}`);
    else if(fmtUpdate.align !== undefined)
      logActivity(`${myName} set alignment to ${fmtUpdate.align || "default"} on ${range}`);

    if (fmtUpdate.fontSize) {
      const newSize = fmtUpdate.fontSize;

      setRowH(prev => {
        const next = { ...prev };
        for (let r = r1; r <= r2; r++) next[r] = Math.max(26, newSize + 14);
        return next;
      });

      const newWidths = colW.map((currentW, c) => {
        const fSize = (c >= c1 && c <= c2) ? newSize : 13;
        let maxPx = 0;
        for (let r = 0; r < numRows; r++) {
          const text = String(grid[r]?.[c] ?? "");
          if (text) maxPx = Math.max(maxPx, measureTextPx(text, fSize));
        }
        const needed = Math.max(60, Math.round(maxPx) + 28);
        return (c >= c1 && c <= c2) ? needed : Math.max(currentW, needed);
      });

      setColW(newWidths);

      if (tableRef.current) {
        const colEls = tableRef.current.querySelectorAll("colgroup col");
        newWidths.forEach((w, ci) => {
          const el = colEls[ci + 1];
          if (el) { el.style.width = w + "px"; el.style.minWidth = w + "px"; }
        });
      }
    }
  }, [activeCell, selStart, selEnd, pushHistory, grid, numRows, colW, logActivity, selLabel, myName]);

  useEffect(() => {
    if (!fillColorOpen) return;
    const h = e => { if (fillColorRef.current && !fillColorRef.current.contains(e.target)) setFillColorOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, [fillColorOpen]);

  useEffect(() => {
    if (!fontColorOpen) return;
    const h = e => { if (fontColorRef.current && !fontColorRef.current.contains(e.target)) setFontColorOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, [fontColorOpen]);

  const matchResults = useMemo(() => {
    if (!findVal) return [];
    const needle = matchCase ? findVal : findVal.toLowerCase();
    const hits = [];
    for (let r = 1; r < numRows; r++) {
      for (let c = 0; c < numCols; c++) {
        const cell = String(grid[r]?.[c] ?? "");
        const hay  = matchCase ? cell : cell.toLowerCase();
        if (hay.includes(needle)) hits.push({ r, c });
      }
    }
    return hits;
  }, [findVal, matchCase, grid, numRows, numCols]);

  const matchSet = useMemo(() => {
    const s = new Set();
    matchResults.forEach(({ r, c }) => s.add(`${r},${c}`));
    return s;
  }, [matchResults]);

  useEffect(() => {
    setMatchIdx(idx => matchResults.length === 0 ? 0 : Math.min(idx, matchResults.length - 1));
  }, [matchResults.length]);

  const findNext = useCallback(() => {
    if (!matchResults.length) return;
    setMatchIdx(i => (i + 1) % matchResults.length);
  }, [matchResults.length]);

  const findPrev = useCallback(() => {
    if (!matchResults.length) return;
    setMatchIdx(i => (i - 1 + matchResults.length) % matchResults.length);
  }, [matchResults.length]);

  const doReplace = useCallback(async () => {
    if (!matchResults.length) return;
    const { r, c } = matchResults[matchIdx] || matchResults[0];
    const cell = String(grid[r]?.[c] ?? "");
    const needle = matchCase ? findVal : findVal.toLowerCase();
    const hay    = matchCase ? cell    : cell.toLowerCase();
    if (!hay.includes(needle)) return;
    const idx    = hay.indexOf(needle);
    const newVal = cell.slice(0, idx) + replaceVal + cell.slice(idx + findVal.length);
    pushHistory(grid);
    setGrid(prev => ({ ...prev, [r]: { ...prev[r], [c]: newVal } }));
    try {
      const task = tasks[r - 1];
      const field = cols[c];
      if (task?._id && field) {
        const res = await axiosInstance.post("/task_assignment/todo_group/task/cell_update",
          { task_id: task._id, group_id: groupId, field, value: newVal });
        if (!res.data?.success) showToast(res.data?.error || "Replace failed", "error");
      }
    } catch { showToast("Replace failed","error"); }
    setMatchIdx(i => Math.min(i, matchResults.length - 2));
    logActivity(`${myName} replaced "${findVal}" with "${replaceVal}" in cell ${cellLabel(matchResults[matchIdx]?.r ?? 0, matchResults[matchIdx]?.c ?? 0)}`);
  }, [matchResults, matchIdx, matchCase, findVal, replaceVal, grid, tasks, cols, pushHistory, groupId, showToast, logActivity, myName]);

  const doReplaceAll = useCallback(async () => {
    if (!matchResults.length) return;
    const needle = matchCase ? findVal : findVal.toLowerCase();
    const newGrid = JSON.parse(JSON.stringify(grid));
    const cellUpdates = [];
    matchResults.forEach(({ r, c }) => {
      const cell = String(grid[r]?.[c] ?? "");
      const hay  = matchCase ? cell : cell.toLowerCase();
      let result = "";
      let last = 0;
      let i = hay.indexOf(needle);
      while (i !== -1) {
        result += cell.slice(last, i) + replaceVal;
        last = i + findVal.length;
        i = hay.indexOf(needle, last);
      }
      result += cell.slice(last);
      newGrid[r][c] = result;
      const task  = tasks[r - 1];
      const field = cols[c];
      if (task?._id && field) cellUpdates.push({ task_id: task._id, field, value: result });
    });
    pushHistory(grid);
    setGrid(newGrid);
    showToast(`Replaced ${matchResults.length} match${matchResults.length !== 1 ? "es" : ""}`, "success");
    logActivity(`${myName} replaced all "${findVal}" → "${replaceVal}" (${matchResults.length} replacement${matchResults.length !== 1 ? "s" : ""})`);
    try {
      await Promise.all(cellUpdates.map(u =>
        axiosInstance.post("/task_assignment/todo_group/task/cell_update",
          { task_id: u.task_id, group_id: groupId, field: u.field, value: u.value })
      ));
    } catch { showToast("Replace All failed","error"); }
  }, [matchResults, matchCase, findVal, replaceVal, grid, tasks, cols, pushHistory, groupId, showToast, logActivity, myName]);

  const selRange = useCallback(() => {
    if(!selStart||!selEnd) return null;
    return {
      r1:Math.min(selStart.r,selEnd.r), r2:Math.max(selStart.r,selEnd.r),
      c1:Math.min(selStart.c,selEnd.c), c2:Math.max(selStart.c,selEnd.c),
    };
  },[selStart,selEnd]);

  const isSelected = useCallback((r,c) => {
    const s = selRange();
    return s && r>=s.r1 && r<=s.r2 && c>=s.c1 && c<=s.c2;
  },[selRange]);

  const autoFillRange = useMemo(() => {
    if (!autoFillDragging || !autoFillEnd || !selStart || !selEnd) return null;
    const srcR1=Math.min(selStart.r,selEnd.r), srcR2=Math.max(selStart.r,selEnd.r);
    const srcC1=Math.min(selStart.c,selEnd.c), srcC2=Math.max(selStart.c,selEnd.c);
    if (autoFillEnd.r > srcR2)       return { r1:srcR2+1, r2:autoFillEnd.r, c1:srcC1, c2:srcC2 };
    if (autoFillEnd.c > srcC2)       return { r1:srcR1,   r2:srcR2,         c1:srcC2+1, c2:autoFillEnd.c };
    if (autoFillEnd.r < srcR1)       return { r1:autoFillEnd.r, r2:srcR1-1, c1:srcC1, c2:srcC2 };
    if (autoFillEnd.c < srcC1)       return { r1:srcR1,   r2:srcR2,         c1:autoFillEnd.c, c2:srcC1-1 };
    return null;
  }, [autoFillDragging, autoFillEnd, selStart, selEnd]);

  const isActive = useCallback((r,c) => activeCell?.r===r && activeCell?.c===c, [activeCell]);
  const isCutCell = useCallback((r,c) => {
    if(!isCut||!copyRange) return false;
    const {r1,r2,c1,c2}=copyRange;
    return r>=Math.min(r1,r2) && r<=Math.max(r1,r2) && c>=Math.min(c1,c2) && c<=Math.max(c1,c2);
  },[isCut,copyRange]);

  const select = useCallback((r1,c1,r2,c2,ar,ac) => {
    setSelStart({r:r1,c:c1}); setSelEnd({r:r2,c:c2});
    setActiveCell({r:ar??r1, c:ac??c1});
  },[]);

  useEffect(() => {
    if (matchResults.length === 0) return;
    const { r, c } = matchResults[matchIdx] || matchResults[0];
    select(r, c, r, c);
  }, [matchIdx, matchResults, select]);

  const selectAll = useCallback(() => {
    if(numRows>0 && numCols>0) select(0,0,numRows-1,numCols-1,0,0);
  },[numRows,numCols,select]);

  const isColSelected = useCallback((ci) => {
    const s = selRange();
    return s && s.c1 <= ci && ci <= s.c2;
  }, [selRange]);

  const isRowFullySelected = useCallback((r) => {
    const s = selRange();
    return !!s && s.r1 <= r && r <= s.r2 && s.c1 === 0 && s.c2 === numCols - 1;
  }, [selRange, numCols]);

  const isAllSelected = useCallback(() => {
    const s = selRange();
    return s && s.r1 === 0 && s.r2 === numRows - 1 && s.c1 === 0 && s.c2 === numCols - 1;
  }, [selRange, numRows, numCols]);

  const navigate = useCallback((r,c,dir) => {
    const nr = dir==="down"?Math.min(r+1,numRows-1):dir==="up"?Math.max(r-1,0):r;
    const nc = dir==="right"?Math.min(c+1,numCols-1):dir==="left"?Math.max(c-1,0):c;
    select(nr,nc,nr,nc);
  },[numRows,numCols,select]);

  const getCellEditable = useCallback((r,c) => {
    if(!canWrite) return false;
    if(r>0 && rowComplete[r]) return false;
    return true;
  },[canWrite,rowComplete]);

  const startEdit = useCallback((r,c,replaceVal) => {
    if(!getCellEditable(r,c)) {
      if(!canWrite) showToast("🔒 Read Only — contact the group admin","warning");
      return;
    }
    setEditCell({r,c});
    const orig = grid[r]?.[c] ?? "";
    setEditOrig(orig);
    setEditVal(replaceVal !== undefined ? replaceVal : orig);
    setTimeout(() => {
      if (!inputRef.current) return;
      inputRef.current.focus();
      const len = inputRef.current.value.length;
      inputRef.current.setSelectionRange(len, len);
    }, 20);
  },[getCellEditable,canWrite,grid,showToast]);

  const commitEdit = useCallback(async (moveDir) => {
    if(!editCell) return;
    const {r,c} = editCell;
    const newVal = editVal;
    setEditCell(null);
    if(newVal === editOrig) {
      if(moveDir) navigate(r,c,moveDir);
      return;
    }
    pushHistory(grid);
    const newGrid = { ...grid, [r]:{ ...(grid[r]||{}), [c]:newVal } };
    setGrid(newGrid);
    if(moveDir) navigate(r,c,moveDir);
    logActivity(`${myName} changed cell ${cellLabel(r,c)}`,
      { before: editOrig, after: newVal });

    const col = cols[c];
    setSaving(true);
    try {
      if(r===0) {
        await axiosInstance.post(`/task_assignment/todo_group/update_header/${groupId}`,
          { col, value:newVal });
      } else {
        const task = tasks[r-1];
        if(task?._id) {
          const res = await axiosInstance.post("/task_assignment/todo_group/task/cell_update",
            { task_id:task._id, group_id:groupId, field:col, value:newVal });
          if(!res.data?.success) showToast(res.data?.error||"Save failed","error");
        }
      }
    } catch { showToast("Save failed","error"); }
    finally { setSaving(false); }
  },[editCell,editVal,editOrig,grid,cols,tasks,groupId,pushHistory,navigate,showToast,logActivity,myName]);

  const cancelEdit = useCallback(() => {
    setEditCell(null);
    setEditVal(editOrig);
  },[editOrig]);

  const performAutoFill = useCallback(async (dragEnd) => {
    if (!selStart || !selEnd || !canWrite || !dragEnd) return;
    const srcR1=Math.min(selStart.r,selEnd.r), srcR2=Math.max(selStart.r,selEnd.r);
    const srcC1=Math.min(selStart.c,selEnd.c), srcC2=Math.max(selStart.c,selEnd.c);

    let fillR1,fillR2,fillC1,fillC2,dir;
    if      (dragEnd.r > srcR2) { dir="down";  fillR1=srcR2+1; fillR2=dragEnd.r;  fillC1=srcC1;   fillC2=srcC2; }
    else if (dragEnd.c > srcC2) { dir="right"; fillR1=srcR1;   fillR2=srcR2;      fillC1=srcC2+1; fillC2=dragEnd.c; }
    else if (dragEnd.r < srcR1) { dir="up";    fillR1=dragEnd.r; fillR2=srcR1-1;  fillC1=srcC1;   fillC2=srcC2; }
    else if (dragEnd.c < srcC1) { dir="left";  fillR1=srcR1;   fillR2=srcR2;      fillC1=dragEnd.c; fillC2=srcC1-1; }
    else return;

    fillR1 = Math.max(1, fillR1);
    if (fillR1 > fillR2 && (dir==="down"||dir==="up"))   return;
    if (fillC1 > fillC2 && (dir==="right"||dir==="left")) return;

    pushHistory(grid);
    const newGrid = JSON.parse(JSON.stringify(grid));
    const saves = [];
    const srcRowCount = srcR2 - srcR1 + 1;
    const srcColCount = srcC2 - srcC1 + 1;

    for (let r = fillR1; r <= fillR2; r++) {
      if (!newGrid[r]) newGrid[r] = {};
      for (let c = fillC1; c <= fillC2; c++) {
        const srcR = srcR1 + ((dir==="down"||dir==="up") ? (r-fillR1) % srcRowCount : (r-srcR1) % srcRowCount);
        const srcC = srcC1 + ((dir==="right"||dir==="left") ? (c-fillC1) % srcColCount : (c-srcC1) % srcColCount);
        const val  = grid[srcR]?.[srcC] ?? "";
        newGrid[r][c] = val;
        if (r >= 1 && tasks[r-1]?._id && cols[c]) {
          saves.push({ taskId: tasks[r-1]._id, field: cols[c], value: val });
        }
      }
    }

    setGrid(newGrid);
    if (dir==="down")  select(srcR1, srcC1, fillR2, srcC2, activeCell?.r??srcR1, activeCell?.c??srcC1);
    if (dir==="right") select(srcR1, srcC1, srcR2,  fillC2, activeCell?.r??srcR1, activeCell?.c??srcC1);
    if (dir==="up")    select(fillR1, srcC1, srcR2, srcC2,  activeCell?.r??srcR1, activeCell?.c??srcC1);
    if (dir==="left")  select(srcR1, fillC1, srcR2, srcC2,  activeCell?.r??srcR1, activeCell?.c??srcC1);

    setSaving(true);
    try {
      await Promise.all(saves.map(s =>
        axiosInstance.post("/task_assignment/todo_group/task/cell_update",
          { task_id:s.taskId, group_id:groupId, field:s.field, value:s.value })
      ));
    } catch { showToast("Some cells failed to save","error"); }
    finally { setSaving(false); }
  }, [selStart, selEnd, grid, tasks, cols, groupId, canWrite, pushHistory, select, activeCell, showToast]);

  useEffect(() => {
    if (!autoFillDragging) return;
    const onMove = (e) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const td = el?.closest("[data-r]");
      if (!td) return;
      const r = parseInt(td.dataset.r, 10);
      const c = parseInt(td.dataset.c, 10);
      if (!isNaN(r) && !isNaN(c)) { autoFillEndRef.current = {r,c}; setAutoFillEnd({r,c}); }
    };
    const onUp = () => {
      autoFillDragRef.current = false;
      setAutoFillDragging(false);
      setAutoFillEnd(null);
      performAutoFill(autoFillEndRef.current);
      autoFillEndRef.current = null;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup",   onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup",   onUp);
    };
  }, [autoFillDragging, performAutoFill]);

  const exportCSV = useCallback(() => {
    const header = cols.map((_,ci) => `"${String(grid[0]?.[ci]??colDisplayNames[cols[ci]]??cols[ci]).replace(/"/g,'""')}"`).join(",");
    const body   = Array.from({ length: tasks.length }, (_, ri) =>
      cols.map((_,ci) => `"${String(grid[ri+1]?.[ci]??"").replace(/"/g,'""')}"`).join(",")
    ).join("\n");
    const blob = new Blob([`${header}\n${body}`],{type:"text/csv"});
    const url  = URL.createObjectURL(blob);
    Object.assign(document.createElement("a"),{ href:url, download:`${groupName||"group"}.csv` }).click();
    URL.revokeObjectURL(url);
    axiosInstance.post("/task_assignment/todo_group/log_export",
      { group_id:groupId, rows_exported:tasks.length }).catch(()=>{});
  },[cols,tasks.length,grid,colDisplayNames,groupName,groupId]);

  const exportXLSX = useCallback(async () => {
    const workbook  = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet1");

    worksheet.columns = cols.map((_,ci) => ({
      width: Math.max(8, Math.round((colW[ci] || 80) / 7.5)),
    }));

    const headerVals = cols.map((_,ci) => grid[0]?.[ci] ?? colDisplayNames[cols[ci]] ?? cols[ci]);
    const headerRow  = worksheet.addRow(headerVals);
    headerRow.height = Math.max(15, (rowH[0] || 26) * 0.75);
    for (let ci = 0; ci < cols.length; ci++) {
      const cell = headerRow.getCell(ci + 1);
      cell.fill      = { type:"pattern", pattern:"solid", fgColor:{ argb:"FFFEF9C3" } };
      cell.font      = { bold:true, size:11 };
      cell.alignment = { vertical:"middle" };
    }

    for (let ri = 0; ri < tasks.length; ri++) {
      const gridRow = ri + 1;
      const rowVals = cols.map((_,ci) => grid[gridRow]?.[ci] ?? "");
      const dataRow = worksheet.addRow(rowVals);
      dataRow.height = Math.max(15, (rowH[gridRow] || 26) * 0.75);

      for (let ci = 0; ci < cols.length; ci++) {
        const cell = dataRow.getCell(ci + 1);
        const key  = `${gridRow}-${ci}`;

        // Background — cellBg/colBg/rowBg store palette NAMES (e.g. "teal"), resolve to hex
        const fmt   = cellFormat[key] || {};
        let bgHex = null;
        if (rowComplete[gridRow]) bgHex = "#e5e7eb";
        else if (cellBg[key])     bgHex = ROW_COLORS[cellBg[key]] || cellBg[key];
        else if (colBg[cols[ci]]) bgHex = ROW_COLORS[colBg[cols[ci]]] || colBg[cols[ci]];
        else if (rowBg[gridRow])  bgHex = ROW_COLORS[rowBg[gridRow]] || null;
        if (fmt.fillColor) bgHex = fmt.fillColor; // toolbar fill color always wins (matches live grid)

        if (bgHex) {
          cell.fill = {
            type:"pattern", pattern:"solid",
            fgColor:{ argb:"FF" + bgHex.replace("#","").toUpperCase() },
          };
        }

        // Font — cellTx/colTx/rowTx store palette NAMES too, resolve to hex the same way
        let txHex = null;
        if (cellTx[key])          txHex = TEXT_COLORS_HEX[cellTx[key]] || cellTx[key];
        else if (colTx[cols[ci]]) txHex = TEXT_COLORS_HEX[colTx[cols[ci]]] || colTx[cols[ci]];
        else if (rowTx[gridRow])  txHex = TEXT_COLORS_HEX[rowTx[gridRow]] || null;
        if (fmt.fontColor) txHex = fmt.fontColor; // toolbar font color always wins (matches live grid)

        const font  = {};
        if (fmt.bold)      font.bold      = true;
        if (fmt.italic)    font.italic    = true;
        if (fmt.underline) font.underline = true;
        if (fmt.fontSize)  font.size      = Math.max(6, Math.round(fmt.fontSize * 0.75));
        if (fmt.fontFamily && fmt.fontFamily !== "Default") font.name = fmt.fontFamily;
        if (txHex) font.color = { argb:"FF" + txHex.replace("#","").toUpperCase() };
        if (Object.keys(font).length) cell.font = font;

        cell.alignment = { vertical:"middle", ...(fmt.align ? { horizontal:fmt.align } : {}) };
      }
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob   = new Blob([buffer], { type:"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url    = URL.createObjectURL(blob);
    Object.assign(document.createElement("a"), { href:url, download:`${groupName||"group"}.xlsx` }).click();
    URL.revokeObjectURL(url);

    axiosInstance.post("/task_assignment/todo_group/log_export",
      { group_id:groupId, rows_exported:tasks.length }).catch(()=>{});
  }, [cols, tasks.length, grid, colDisplayNames, groupName, groupId,
      rowBg, rowComplete, cellBg, colBg, cellTx, colTx, rowTx, cellFormat, colW, rowH]);

  const markComplete = useCallback(async (r) => {
    const task = tasks[r-1];
    if(!task?._id) return;
    setRowComplete(prev=>({...prev,[r]:true}));
    try {
      const res = await axiosInstance.post("/task_assignment/todo_group/task/complete",
        { task_id:task._id, group_id:groupId });
      if(!res.data?.success) { setRowComplete(prev=>({...prev,[r]:false})); showToast(res.data?.error||"Error","error"); }
      else showToast("Marked as done! ✅");
    } catch { setRowComplete(prev=>({...prev,[r]:false})); }
  },[tasks,groupId,showToast]);

  const undoComplete = useCallback(async (r) => {
    const task = tasks[r-1];
    if(!task?._id) return;
    setRowComplete(prev=>({...prev,[r]:false}));
    try {
      const res = await axiosInstance.post("/task_assignment/todo_group/task/undo",
        { task_id:task._id, group_id:groupId });
      if(!res.data?.success) { setRowComplete(prev=>({...prev,[r]:true})); showToast(res.data?.error||"Error","error"); }
      else showToast("Row reopened!");
    } catch { setRowComplete(prev=>({...prev,[r]:true})); }
  },[tasks,groupId,showToast]);

  // ── Pending operations: what is waiting for the server, shown on the grid + footer ──
  const tasksRef = useRef(tasks); tasksRef.current = tasks;
  const colsRef  = useRef(cols);  colsRef.current  = cols;
  const [pendingDelRows, setPendingDelRows] = useState(() => new Set());   // task _ids
  const [pendingDelCols, setPendingDelCols] = useState(() => new Set());   // col keys
  const [opsRunning,     setOpsRunning]     = useState(0);
  const [sheetStatus,    setSheetStatus]    = useState(null);              // {kind:'busy'|'ok'|'error', text}
  const statusTimer = useRef(null);

  const markRows = useCallback((ids, on) => setPendingDelRows(prev => {
    const n = new Set(prev); ids.forEach(id => on ? n.add(id) : n.delete(id)); return n;
  }), []);
  const markCols = useCallback((keys, on) => setPendingDelCols(prev => {
    const n = new Set(prev); keys.forEach(k => on ? n.add(k) : n.delete(k)); return n;
  }), []);

  // beginOp("Deleting 3 rows…") → returns end(ok, text); drives the progress bar + footer status
  const beginOp = useCallback((label) => {
    setOpsRunning(n => n + 1);
    setSheetStatus({ kind:"busy", text:label });
    clearTimeout(statusTimer.current);
    let done = false;
    return (ok, text) => {
      if(done) return; done = true;
      setOpsRunning(n => Math.max(0, n - 1));
      setSheetStatus({ kind: ok ? "ok" : "error", text });
      clearTimeout(statusTimer.current);
      statusTimer.current = setTimeout(() => setSheetStatus(st => st && st.kind !== "busy" ? null : st), ok ? 3000 : 6000);
    };
  }, []);
  useEffect(() => () => clearTimeout(statusTimer.current), []);

  // Remove rows (by task _id) / columns (by key) from local state once the server confirmed.
  const removeRowsNow = useCallback((ids) => {
    const idSet  = new Set(ids);
    const sorted = tasksRef.current.map((t,i) => idSet.has(t?._id) ? i+1 : -1).filter(r => r > 0).sort((a,b)=>b-a);
    if(!sorted.length) return;
    setTasks(prev  => prev.filter(t => !idSet.has(t?._id)));
    setGrid(prev   => { let g=prev; sorted.forEach(r=>{ g=deleteRowGrid(g,r); }); return g; });
    setRowComplete(prev=>{ let m=prev; sorted.forEach(r=>{m=shiftMapDel(m,r);}); return m; });
    setRowBg(prev  =>{ let m=prev; sorted.forEach(r=>{m=shiftMapDel(m,r);}); return m; });
    setRowTx(prev  =>{ let m=prev; sorted.forEach(r=>{m=shiftMapDel(m,r);}); return m; });
    setCellBg(prev =>{ let m=prev; sorted.forEach(r=>{m=shiftCellDel(m,r);}); return m; });
    setCellTx(prev =>{ let m=prev; sorted.forEach(r=>{m=shiftCellDel(m,r);}); return m; });
  }, []);
  const removeColsNow = useCallback((keys) => {
    const keySet  = new Set(keys);
    const indices = colsRef.current.map((k,i) => keySet.has(k) ? i : -1).filter(i => i >= 0);
    const sorted  = [...indices].sort((a,b)=>b-a);
    if(!sorted.length) return;
    setCols(prev  => prev.filter(k => !keySet.has(k)));
    setColW(prev  => prev.filter((_,i) => !indices.includes(i)));
    setGrid(prev  => { let g=prev; sorted.forEach(ci=>{ g=removeColGrid(g,ci); }); return g; });
    setColBg(prev => { const n={...prev}; keys.forEach(k=>delete n[k]); return n; });
    setColTx(prev => { const n={...prev}; keys.forEach(k=>delete n[k]); return n; });
    setCellBg(prev=> { let m=prev; sorted.forEach(ci=>{m=shiftCellColDel(m,ci);}); return m; });
    setCellTx(prev=> { let m=prev; sorted.forEach(ci=>{m=shiftCellColDel(m,ci);}); return m; });
  }, []);

  // Render helpers: rows/cols without a server id yet are being added; marked ones are being deleted.
  const rowPendKind = (r) => {
    if(r < 1) return null;
    const t = tasks[r-1];
    if(!t) return null;
    if(t._id && pendingDelRows.has(t._id)) return "del";
    return t._id ? null : "add";
  };
  const colPendKind = (c) => {
    const k = cols[c];
    if(k === undefined) return null;
    if(pendingDelCols.has(k)) return "del";
    return String(k).startsWith("__tmp") ? "add" : null;
  };
  const pendCls = (kind) => kind === "del" ? " sheet-pending-del" : kind === "add" ? " sheet-pending-add" : "";
  const cellPendCls = (r, c) => {
    const rk = rowPendKind(r), ck = colPendKind(c);
    return pendCls(rk === "del" || ck === "del" ? "del" : (rk || ck));
  };

  const deleteRow = useCallback(async (r) => {
    if(!window.confirm("Delete this row permanently?")) return;
    const task = tasks[r-1];
    if(!task?._id) return;
    setCtx(null);
    markRows([task._id], true);
    const endOp = beginOp(`Deleting row ${r}…`);
    const fd = new FormData();
    fd.append("task_id", task._id); fd.append("group_id", groupId);
    try {
      const res = await axiosInstance.post("/task_assignment/todo_group/task/delete", fd,
        { headers:{"Content-Type":"multipart/form-data"} });
      if(res.data?.success) {
        removeRowsNow([task._id]);
        endOp(true, "✓ Row deleted");
        showToast("Row deleted!"); logActivity(`${myName} deleted row ${r}`);
      } else {
        endOp(false, res.data?.error || "Row not deleted");
        showToast(res.data?.error||"Error","error");
      }
    } catch {
      endOp(false, "Server error — row not deleted");
      showToast("Server error","error");
    } finally {
      markRows([task._id], false);
    }
  },[tasks,groupId,showToast,logActivity,myName,beginOp,markRows,removeRowsNow]);

  const bulkDeleteRows = useCallback(async (rowIndices, opts={}) => {
    if(!rowIndices.length) return;
    if(!opts.skipConfirm && !window.confirm(`Delete ${rowIndices.length} row(s) permanently? This cannot be undone.`)) return;
    const taskIds  = rowIndices.map(r=>tasks[r-1]?._id).filter(Boolean);
    if(!taskIds.length) return;
    const history  = {
      tasks:       JSON.parse(JSON.stringify(tasks)),
      rowComplete: JSON.parse(JSON.stringify(rowComplete)),
      rowBg:       JSON.parse(JSON.stringify(rowBg)),
      rowTx:       JSON.parse(JSON.stringify(rowTx)),
      cellBg:      JSON.parse(JSON.stringify(cellBg)),
      cellTx:      JSON.parse(JSON.stringify(cellTx)),
      deletedObjs: rowIndices.map(r => ({
        rowIdx:  r,
        task:    tasks[r-1] ? JSON.parse(JSON.stringify(tasks[r-1])) : null,
        gridRow: grid[r]    ? JSON.parse(JSON.stringify(grid[r]))    : {},
      })),
    };
    const gridSnap = grid;
    setCtx(null);
    markRows(taskIds, true);
    const n = taskIds.length;
    const endOp = beginOp(`Deleting ${n} row${n!==1?"s":""}…`);
    try {
      const res = await axiosInstance.post("/task_assignment/todo_group/task/delete",
        { group_id:groupId, task_ids:taskIds });
      if(res.data?.success) {
        pushHistory(gridSnap, history);
        removeRowsNow(taskIds);
        endOp(true, `✓ ${res.data.deleted||n} row${n!==1?"s":""} deleted`);
        showToast(`${res.data.deleted||n} row(s) deleted!`);
        logActivity(`${myName} deleted ${n} row${n!==1?"s":""}`);
      } else {
        endOp(false, res.data?.error || "Rows not deleted");
        showToast(res.data?.error||"Error","error");
      }
    } catch {
      endOp(false, "Server error — rows not deleted");
      showToast("Server error","error");
    } finally {
      markRows(taskIds, false);
    }
  },[tasks,grid,rowComplete,rowBg,rowTx,cellBg,cellTx,groupId,pushHistory,showToast,logActivity,myName,beginOp,markRows,removeRowsNow]);

  const insertRow = useCallback(async (r, pos) => {
    const insertIdx = pos === "above" ? Math.max(0, r-1) : r;
    const gridRow   = insertIdx + 1;
    const snap = { tasks, grid, rowComplete, rowBg, rowTx, cellBg, cellTx };
    const tempTask  = { _id: null, row_data:{}, status:"active" };
    const newTasks  = [...tasks.slice(0, insertIdx), tempTask, ...tasks.slice(insertIdx)];
    setTasks(newTasks);
    setGrid(prev => insertRowGrid(prev, gridRow, cols.length));
    setRowComplete(prev => shiftMapIns(prev, gridRow));
    setRowBg(prev => shiftMapIns(prev, gridRow));
    setRowTx(prev => shiftMapIns(prev, gridRow));
    setCellBg(prev => shiftCellIns(prev, gridRow));
    setCellTx(prev => shiftCellIns(prev, gridRow));
    setCtx(null);
    const endOp = beginOp(`Adding a row ${pos} row ${r}…`);
    const ref = tasks[r-1];
    const fd  = new FormData();
    fd.append("group_id", groupId);
    fd.append("insert_position", pos);
    if(ref?._id) fd.append("reference_task_id", ref._id);
    try {
      const res = await axiosInstance.post("/task_assignment/todo_group/task/add", fd,
        { headers:{"Content-Type":"multipart/form-data"} });
      if(res.data?.success) {
        showToast("Row inserted!");
        endOp(true, "✓ Row added");
        logActivity(`${myName} inserted 1 row ${pos} row ${r}`);
        const rt = res.data.task;
        if(rt?._id) {
          setTasks(prev => prev.map((t,i) => i === insertIdx ? rt : t));
        } else {
          axiosInstance.get(`/task_assignment/todo_group/tasks/${groupId}`)
            .then(r2 => { if(r2.data?.success) setTasks(r2.data.tasks||[]); })
            .catch(()=>{});
        }
      } else {
        setTasks(snap.tasks); setGrid(snap.grid); setRowComplete(snap.rowComplete);
        setRowBg(snap.rowBg); setRowTx(snap.rowTx); setCellBg(snap.cellBg); setCellTx(snap.cellTx);
        showToast(res.data?.error||"Error","error");
        endOp(false, res.data?.error || "Row not added");
      }
    } catch {
      setTasks(snap.tasks); setGrid(snap.grid); setRowComplete(snap.rowComplete);
      setRowBg(snap.rowBg); setRowTx(snap.rowTx); setCellBg(snap.cellBg); setCellTx(snap.cellTx);
      showToast("Server error","error");
      endOp(false, "Server error — row not added");
    }
  },[tasks,cols,grid,rowComplete,rowBg,rowTx,cellBg,cellTx,groupId,showToast,buildGrid,logActivity,myName,beginOp]);

  const addColumn = useCallback(async (insertAfter) => {
    const ci      = insertAfter ? (cols.indexOf(insertAfter) + 1) : cols.length;
    const tempKey = `__tmp_${Date.now()}`;
    const snap    = { cols, grid, colW };
    const newCols = [...cols.slice(0, ci), tempKey, ...cols.slice(ci)];
    setCols(newCols);
    setColW(prev => [...prev.slice(0, ci), 140, ...prev.slice(ci)]);
    setGrid(prev => insertColGrid(prev, ci));
    setCtx(null);
    const endOp = beginOp("Adding a column…");
    const fd = new FormData();
    fd.append("group_id", groupId);
    if(insertAfter) fd.append("insert_after", insertAfter);
    try {
      const res = await axiosInstance.post("/task_assignment/todo_group/column/add", fd,
        { headers:{"Content-Type":"multipart/form-data"} });
      if(res.data?.success) {
        showToast("Column added!");
        endOp(true, "✓ Column added");
        logActivity(`${myName} added a new column at position ${ci + 1}`);
        const realKey = res.data.col_key || res.data.column_key || res.data.new_col_key;
        if(realKey) {
          setCols(prev => prev.map(k => k === tempKey ? realKey : k));
        } else {
          axiosInstance.get(`/task_assignment/todo_group/tasks/${groupId}`)
            .then(r2 => { if(r2.data?.success) setCols(r2.data.columns||[]); })
            .catch(()=>{});
        }
      } else {
        setCols(snap.cols); setColW(snap.colW); setGrid(snap.grid);
        showToast(res.data?.error||"Error","error");
        endOp(false, res.data?.error || "Column not added");
      }
    } catch {
      setCols(snap.cols); setColW(snap.colW); setGrid(snap.grid);
      showToast("Server error","error");
      endOp(false, "Server error — column not added");
    }
  },[cols,grid,colW,groupId,showToast,logActivity,myName,beginOp]);

  const bulkInsertRows = useCallback(async (r, pos, count) => {
    if(count === 1) { insertRow(r, pos); return; }
    showToast(`Inserting ${count} rows...`);
    const endOp = beginOp(`Adding ${count} rows…`);
    const insertIdx = pos === "above" ? Math.max(0, r-1) : r;
    const gridRow   = insertIdx + 1;
    const tempTasks = Array.from({length:count}, () => ({ _id:null, row_data:{}, status:"active" }));
    setTasks(prev => [...prev.slice(0, insertIdx), ...tempTasks, ...prev.slice(insertIdx)]);
    setGrid(prev => { let g=prev; for(let i=0;i<count;i++) g=insertRowGrid(g,gridRow+i,cols.length); return g; });
    setRowComplete(prev=>{ let m=prev; for(let i=0;i<count;i++) m=shiftMapIns(m,gridRow+i); return m; });
    setRowBg(prev =>{ let m=prev; for(let i=0;i<count;i++) m=shiftMapIns(m,gridRow+i); return m; });
    setRowTx(prev =>{ let m=prev; for(let i=0;i<count;i++) m=shiftMapIns(m,gridRow+i); return m; });
    setCellBg(prev=>{ let m=prev; for(let i=0;i<count;i++) m=shiftCellIns(m,gridRow+i); return m; });
    setCellTx(prev=>{ let m=prev; for(let i=0;i<count;i++) m=shiftCellIns(m,gridRow+i); return m; });
    // One request for all rows (was one request per row: slow, and still running
    // in the background when the user refreshed, so the row count kept changing).
    const refId = tasks[r-1]?._id || null;
    let ok = false;
    try {
      const res = await axiosInstance.post("/task_assignment/todo_group/task/bulk_add", {
        group_id: groupId, count,
        ...(refId ? { reference_task_id: refId, insert_position: pos } : {}),
      });
      ok = !!res.data?.success;
      if(!ok) showToast(res.data?.error || "Could not insert rows","error");
    } catch(err) {
      showToast(err?.response?.data?.error || "Server error","error");
    }
    try {
      const res = await axiosInstance.get(`/task_assignment/todo_group/tasks/${groupId}`);
      if(res.data?.success && res.data.tasks) setTasks(res.data.tasks);
    } catch {}
    if(ok) {
      showToast(`${count} rows inserted!`);
      logActivity(`${myName} inserted ${count} rows ${pos} row ${r}`);
      endOp(true, `✓ ${count} rows added`);
    } else {
      endOp(false, "Rows not added");
    }
  },[tasks,cols,grid,rowComplete,rowBg,rowTx,cellBg,cellTx,groupId,insertRow,showToast,logActivity,myName,beginOp]);

  const bulkInsertCols = useCallback(async (ci, pos, count) => {
    if(count === 1) {
      if(pos === "left") { if(ci>0) addColumn(cols[ci-1]); else showToast("Cannot insert before the first column","error"); }
      else addColumn(cols[ci]);
      return;
    }
    showToast(`Inserting ${count} columns...`);
    const endOp = beginOp(`Adding ${count} columns…`);
    const insertAt = pos === "left" ? ci : ci + 1;
    const insertAfterKey = pos === "left" ? cols[ci-1] : cols[ci];
    for(let i=0; i<count; i++) {
      const tempKey = `__tmp_bulk_${i}`;
      setCols(prev => { const n=[...prev]; n.splice(insertAt+i, 0, tempKey); return n; });
      setColW(prev => { const n=[...prev]; n.splice(insertAt+i, 0, 140); return n; });
      setGrid(prev => insertColGrid(prev, insertAt+i));
    }
    let currentInsertAfter = insertAfterKey;
    for(let i=0; i<count; i++) {
      const fd = new FormData();
      fd.append("group_id", groupId);
      if(currentInsertAfter) fd.append("insert_after", currentInsertAfter);
      try {
        const res = await axiosInstance.post("/task_assignment/todo_group/column/add", fd,
          { headers:{"Content-Type":"multipart/form-data"} });
        currentInsertAfter = res.data?.col_key || res.data?.column_key || res.data?.new_col_key || currentInsertAfter;
      } catch {}
    }
    try {
      const res = await axiosInstance.get(`/task_assignment/todo_group/tasks/${groupId}`);
      if(res.data?.success) { if(res.data.columns) setCols(res.data.columns); }
    } catch {}
    showToast(`${count} columns inserted!`);
    endOp(true, `✓ ${count} columns added`);
    logActivity(`${myName} inserted ${count} columns ${pos === "left" ? "to the left of" : "to the right of"} column ${colLetter(pos === "left" ? ci : ci + 1)}`);
  },[cols,grid,groupId,addColumn,showToast,logActivity,myName,beginOp]);

  // Empty-sheet panel: the user picks how many columns / rows to create.
  // Rows left over after every column was deleted carry no visible data, so they go.
  const SHEET_MAX_COLS = 50, SHEET_MAX_ROWS = 200;
  const [startingSheet, setStartingSheet] = useState(false);
  const [newSheetCols,  setNewSheetCols]  = useState("5");
  const [newSheetRows,  setNewSheetRows]  = useState("10");
  const startSheet = useCallback(async () => {
    if(startingSheet) return;
    const needCols = cols.length === 0;
    const colCount = needCols ? parseInt(newSheetCols, 10) : 0;
    const rowCount = parseInt(newSheetRows, 10);
    if(needCols && !(colCount >= 1 && colCount <= SHEET_MAX_COLS)) {
      showToast(`Columns must be between 1 and ${SHEET_MAX_COLS}`,"error"); return;
    }
    if(!(rowCount >= 1 && rowCount <= SHEET_MAX_ROWS)) {
      showToast(`Rows must be between 1 and ${SHEET_MAX_ROWS}`,"error"); return;
    }
    setStartingSheet(true);
    showToast(`Creating ${needCols ? `${colCount} column${colCount!==1?"s":""} and ` : ""}${rowCount} row${rowCount!==1?"s":""}...`);
    try {
      const orphanIds = needCols ? tasks.map(t=>t?._id).filter(Boolean) : [];
      if(orphanIds.length) {
        await axiosInstance.post("/task_assignment/todo_group/task/delete", { group_id:groupId, task_ids:orphanIds });
      }
      let insertAfter = cols[cols.length-1];
      for(let i=0; i<colCount; i++) {
        const fd = new FormData();
        fd.append("group_id", groupId);
        if(insertAfter) fd.append("insert_after", insertAfter);
        const res = await axiosInstance.post("/task_assignment/todo_group/column/add", fd,
          { headers:{"Content-Type":"multipart/form-data"} });
        insertAfter = res.data?.col_key || res.data?.column_key || res.data?.new_col_key || insertAfter;
      }
      let refId = null;
      for(let i=0; i<rowCount; i++) {
        const fd = new FormData();
        fd.append("group_id", groupId);
        fd.append("insert_position", "below");
        if(refId) fd.append("reference_task_id", refId);
        const res = await axiosInstance.post("/task_assignment/todo_group/task/add", fd,
          { headers:{"Content-Type":"multipart/form-data"} });
        refId = res.data?.task?._id || refId;
      }
      showToast("Sheet created!");
      logActivity(`${myName} created ${needCols ? `${colCount} column${colCount!==1?"s":""} and ` : ""}${rowCount} row${rowCount!==1?"s":""} in an empty sheet`);
    } catch {
      showToast("Server error","error");
    } finally {
      setStartingSheet(false);
      onReload?.();
    }
  },[startingSheet,cols,tasks,newSheetCols,newSheetRows,groupId,showToast,logActivity,myName,onReload]);

  // Empty-sheet panel: fill the sheet from a CSV/XLSX (first row = column names), like group create.
  const SHEET_IMPORT_MAX_MB = 10;
  const [importFile, setImportFile] = useState(null);
  const [importing,  setImporting]  = useState(false);
  const importInputRef = useRef(null);
  const importSheetFile = useCallback(async () => {
    if(!importFile || importing) return;
    const name = importFile.name.toLowerCase();
    if(!name.endsWith(".csv") && !name.endsWith(".xlsx")) {
      showToast("Only .csv or .xlsx files are supported","error"); return;
    }
    if(importFile.size > SHEET_IMPORT_MAX_MB * 1024 * 1024) {
      showToast(`File is too large (max ${SHEET_IMPORT_MAX_MB} MB)`,"error"); return;
    }
    setImporting(true);
    showToast(`Importing ${importFile.name}...`);
    try {
      const fd = new FormData();
      fd.append("group_id", groupId);
      fd.append("group_file", importFile);
      const res = await axiosInstance.post("/task_assignment/todo_group/import", fd,
        { headers:{"Content-Type":"multipart/form-data"} });
      if(res.data?.success) {
        showToast(`Imported ${res.data.columns} column${res.data.columns!==1?"s":""} and ${res.data.rows} row${res.data.rows!==1?"s":""}!`);
        setImportFile(null);
        onReload?.();
      } else {
        showToast(res.data?.error || "Import failed","error");
      }
    } catch(err) {
      showToast(err?.response?.data?.error || "Import failed","error");
    } finally {
      setImporting(false);
    }
  },[importFile,importing,groupId,showToast,onReload]);

  const deleteColumn = useCallback(async (colKey) => {
    if(!window.confirm(`Delete column "${colKey}" and all its data?`)) return;
    if(cols.indexOf(colKey) === -1) return;
    setCtx(null);
    markCols([colKey], true);
    const endOp = beginOp("Deleting column…");
    const fd = new FormData();
    fd.append("group_id", groupId); fd.append("col_keys[]", colKey);
    try {
      const res = await axiosInstance.post("/task_assignment/todo_group/column/delete", fd,
        { headers:{"Content-Type":"multipart/form-data"} });
      if(res.data?.success) {
        removeColsNow([colKey]);
        endOp(true, "✓ Column deleted");
        showToast("Column deleted!"); logActivity(`${myName} deleted column "${colKey}"`);
      } else {
        endOp(false, res.data?.error || "Column not deleted");
        showToast(res.data?.error||"Error","error");
      }
    } catch {
      endOp(false, "Server error — column not deleted");
      showToast("Server error","error");
    } finally {
      markCols([colKey], false);
    }
  },[cols,groupId,showToast,logActivity,myName,beginOp,markCols,removeColsNow]);

  const bulkDeleteColumns = useCallback(async (colIndices) => {
    if(!colIndices.length) return;
    const colKeys = colIndices.map(ci=>cols[ci]).filter(Boolean);
    if(!colKeys.length) return;
    if(!window.confirm(`Delete ${colKeys.length} column(s) and all their data? This cannot be undone.`)) return;
    setCtx(null);
    markCols(colKeys, true);
    const n = colKeys.length;
    const endOp = beginOp(`Deleting ${n} column${n!==1?"s":""}…`);
    try {
      const fd = new FormData();
      fd.append("group_id", groupId);
      colKeys.forEach(k=>fd.append("col_keys[]",k));
      const res = await axiosInstance.post("/task_assignment/todo_group/column/delete", fd,
        { headers:{"Content-Type":"multipart/form-data"} });
      if(res.data?.success) {
        removeColsNow(colKeys);
        endOp(true, `✓ ${n} column${n!==1?"s":""} deleted`);
        showToast(`${n} column(s) deleted!`);
        logActivity(`${myName} deleted ${n} column(s): ${colKeys.join(", ")}`);
      } else {
        endOp(false, res.data?.error || "Columns not deleted");
        showToast(res.data?.error||"Error","error");
      }
    } catch {
      endOp(false, "Server error — columns not deleted");
      showToast("Server error","error");
    } finally {
      markCols(colKeys, false);
    }
  },[cols,groupId,showToast,logActivity,myName,beginOp,markCols,removeColsNow]);

  const openRemoveDuplicates = useCallback(() => {
    setRemoveDupWarn("expand");
    setRemoveDupHeaders(true);
    setRemoveDupStep(1);
  },[]);

  const removeDupProceed = useCallback(() => {
    const s = selRange();
    if(removeDupWarn === "current" && s) {
      const range = Array.from({length:s.c2-s.c1+1},(_,i)=>s.c1+i)
        .filter(ci => !removeDupHeaders || String(grid[0]?.[ci]??"").trim() !== "");
      setRemoveDupDisplayCols(range);
      setRemoveDupCols(range);
    } else {
      const all = cols.map((_,i)=>i).filter(ci=>
        !removeDupHeaders || String(grid[0]?.[ci]??"").trim() !== ""
      );
      setRemoveDupDisplayCols(all);
      setRemoveDupCols(all);
    }
    setRemoveDupStep(2);
  },[removeDupWarn,removeDupHeaders,selRange,cols,grid]);

  const performRemoveDuplicates = useCallback(async () => {
    if(!canWrite) { showToast("Read Only","warning"); return; }
    const checkCols = removeDupCols.length ? removeDupCols : cols.map((_,i)=>i);
    const rows = removeDupHeaders ? visibleDataRows.filter(r=>r>0) : visibleDataRows;
    const seen = new Set();
    const toDelete = [];
    for(const r of rows) {
      const key = checkCols.map(ci=>String(grid[r]?.[ci]??"").trim()).join("|||");
      if(seen.has(key)) toDelete.push(r);
      else seen.add(key);
    }
    setRemoveDupStep(0);
    if(!toDelete.length) {
      setRemoveDupResult({ removed:0, remaining: rows.length });
      setRemoveDupStep(3);
      return;
    }
    await bulkDeleteRows(toDelete, { skipConfirm:true });
    setRemoveDupResult({ removed: toDelete.length, remaining: rows.length - toDelete.length });
    setRemoveDupStep(3);
    logActivity(`${myName} removed ${toDelete.length} duplicate row${toDelete.length!==1?"s":""} (${rows.length - toDelete.length} remaining)`);
  },[canWrite,removeDupCols,removeDupHeaders,cols,visibleDataRows,grid,bulkDeleteRows,showToast,logActivity,myName]);

  const applyRowBg = useCallback(async (r,color) => {
    const task = tasks[r-1];
    if(!task?._id) return;
    setRowBg(prev=>({...prev,[r]:color==="none"?"":color}));
    logActivity(`${myName} set row ${r} background to ${color==="none"?"none":color}`);
    await axiosInstance.post("/task_assignment/todo_group/task/cell_update",
      { task_id:task._id, group_id:groupId, field:"__row_color__", value:color==="none"?"":color })
      .catch(()=>{});
  },[tasks,groupId,logActivity,myName]);

  const applyRowTx = useCallback(async (r,color) => {
    const task = tasks[r-1];
    if(!task?._id) return;
    setRowTx(prev=>({...prev,[r]:color==="none"?"":color}));
    logActivity(`${myName} set row ${r} text color to ${color==="none"?"none":color}`);
    await axiosInstance.post("/task_assignment/todo_group/task/cell_update",
      { task_id:task._id, group_id:groupId, field:"__row_text_color__", value:color==="none"?"":color })
      .catch(()=>{});
  },[tasks,groupId,logActivity,myName]);

  const applyCellBg = useCallback(async (r,c,color) => {
    const task = tasks[r-1]; const col = cols[c];
    if(!task?._id||!col) return;
    const key=`${r}-${c}`;
    setCellBg(prev=>({...prev,[key]:color==="none"?"":color}));
    logActivity(`${myName} set cell ${cellLabel(r,c)} background to ${color==="none"?"none":color}`);
    await axiosInstance.post("/task_assignment/todo_group/task/cell_update",
      { task_id:task._id, group_id:groupId, field:`__cell_bg__${col}`, value:color==="none"?"":color })
      .catch(()=>{});
  },[tasks,cols,groupId,logActivity,myName]);

  const applyCellTx = useCallback(async (r,c,color) => {
    const task = tasks[r-1]; const col = cols[c];
    if(!task?._id||!col) return;
    const key=`${r}-${c}`;
    setCellTx(prev=>({...prev,[key]:color==="none"?"":color}));
    logActivity(`${myName} set cell ${cellLabel(r,c)} text color to ${color==="none"?"none":color}`);
    await axiosInstance.post("/task_assignment/todo_group/task/cell_update",
      { task_id:task._id, group_id:groupId, field:`__cell_text__${col}`, value:color==="none"?"":color })
      .catch(()=>{});
  },[tasks,cols,groupId,logActivity,myName]);

  const applyColBg = useCallback(async (colKey,color) => {
    setColBg(prev=>({...prev,[colKey]:color==="none"?"":color}));
    logActivity(`${myName} set column "${colKey}" background to ${color==="none"?"none":color}`);
    await axiosInstance.post("/task_assignment/todo_group/column/style",
      { group_id:groupId, col_key:colKey, style_type:"bg", value:color==="none"?"":color })
      .catch(()=>{});
  },[groupId,logActivity,myName]);

  const applyColTx = useCallback(async (colKey,color) => {
    setColTx(prev=>({...prev,[colKey]:color==="none"?"":color}));
    logActivity(`${myName} set column "${colKey}" text color to ${color==="none"?"none":color}`);
    await axiosInstance.post("/task_assignment/todo_group/column/style",
      { group_id:groupId, col_key:colKey, style_type:"text", value:color==="none"?"":color })
      .catch(()=>{});
  },[groupId,logActivity,myName]);

  const doCopy = useCallback((cut=false) => {
    const s = selRange();
    if(!s) return;
    const data=[];
    for(let r=s.r1;r<=s.r2;r++){
      const row=[];
      for(let c=s.c1;c<=s.c2;c++) row.push(grid[r]?.[c]??"");
      data.push(row);
    }
    navigator.clipboard.writeText(data.map(r=>r.join("\t")).join("\n")).catch(()=>{});
    setCopyRange(s); setIsCut(cut);
  },[selRange,grid]);

  const doPaste = useCallback(async () => {
    if(!canWrite) { showToast("🔒 Read Only","warning"); return; }
    if(!activeCell) return;
    const text = await navigator.clipboard.readText().catch(()=>"");
    if(!text) return;

    let pasteRows = text.split("\n").map(r=>r.split("\t"));
    while(pasteRows.length>0 && pasteRows[pasteRows.length-1].every(v=>!v)) pasteRows.pop();
    if(!pasteRows.length) return;

    const {r:startR, c:startC} = activeCell;
    const needRows = Math.max(0, startR + pasteRows.length - numRows);
    const needCols = Math.max(0, startC + (pasteRows[0]?.length||1) - numCols);

    let localCols  = [...cols];
    let localColW  = [...colW];
    let localTasks = [...tasks];

    if(needRows > 0 || needCols > 0) {
      try {
        const res = await axiosInstance.post("/task_assignment/todo_group/auto_grow",
          { group_id:groupId, need_rows:needRows, need_cols:needCols });
        if(!res.data?.success) { showToast(res.data?.error||"Auto-grow failed","error"); return; }
        const newColKeys = res.data.new_col_keys || [];
        const newTaskIds = res.data.new_task_ids || [];
        if(newColKeys.length) {
          localCols = [...localCols, ...newColKeys];
          localColW = [...localColW, ...newColKeys.map(()=>140)];
        }
        if(newTaskIds.length) {
          localTasks = [...localTasks, ...newTaskIds.map(id=>({ _id:id, row_data:{}, status:"open" }))];
        }
      } catch { showToast("Auto-grow failed","error"); return; }
    }

    pushHistory(grid);
    const newGrid = JSON.parse(JSON.stringify(grid));
    localTasks.forEach((_t, ti) => {
      const gr = ti + 1;
      if(!newGrid[gr]) newGrid[gr] = {};
      localCols.forEach((_,ci) => { if(newGrid[gr][ci]===undefined) newGrid[gr][ci]=""; });
    });

    const updates = [];
    const totalRows = localTasks.length + 1;
    const totalCols = localCols.length;
    pasteRows.forEach((pr, ri) => {
      pr.forEach((val, ci) => {
        const tr=startR+ri, tc=startC+ci;
        if(tr>=totalRows||tc>=totalCols) return;
        if(rowComplete[tr]) return;
        const nv=val.trim();
        if(!newGrid[tr]) newGrid[tr]={};
        newGrid[tr][tc]=nv;
        const task=localTasks[tr-1]; const col=localCols[tc];
        if(task?._id && col) updates.push({task_id:task._id, col_key:col, value:nv});
      });
    });

    if(isCut && copyRange) {
      const {r1,r2,c1,c2}=copyRange;
      for(let r=Math.min(r1,r2);r<=Math.max(r1,r2);r++)
        for(let c=Math.min(c1,c2);c<=Math.max(c1,c2);c++){
          if(rowComplete[r]) continue;
          if(!newGrid[r]) newGrid[r]={};
          newGrid[r][c]="";
          const t=localTasks[r-1]; const col=localCols[c];
          if(t?._id && col) updates.push({task_id:t._id,col_key:col,value:""});
        }
      setCopyRange(null); setIsCut(false);
    }

    if(needCols>0) { setCols(localCols); setColW(localColW); }
    if(needRows>0) setTasks(localTasks);
    setGrid(newGrid);

    if(updates.length) {
      await axiosInstance.post("/task_assignment/todo_group/task/bulk_cell_update",
        { group_id:groupId, updates }).catch(()=>{});
    }
    select(startR,startC,
      Math.min(startR+pasteRows.length-1,totalRows-1),
      Math.min(startC+(pasteRows[0]?.length||1)-1,totalCols-1),
      startR,startC);
  },[canWrite,activeCell,grid,numRows,numCols,rowComplete,tasks,cols,colW,groupId,isCut,copyRange,pushHistory,select,showToast]);

  const doUndo = useCallback(async () => {
    if(!history.length) return;
    const prev = history[history.length-1];
    setFuture(f=>[{
      grid:        JSON.parse(JSON.stringify(grid)),
      mergedCells: JSON.parse(JSON.stringify(mergedCellsRef.current)),
      cellFormat:  JSON.parse(JSON.stringify(cellFormatRef.current)),
    },...f.slice(0,29)]);
    setHistory(h=>h.slice(0,-1));
    setGrid(prev.grid);
    setMergedCells(prev.mergedCells || []);
    setCellFormat(prev.cellFormat  || {});

    if(prev.rowSnap) {
      const rs = prev.rowSnap;
      setTasks(rs.tasks);
      setRowComplete(rs.rowComplete || {});
      setRowBg(rs.rowBg || {});
      setRowTx(rs.rowTx || {});
      setCellBg(rs.cellBg || {});
      setCellTx(rs.cellTx || {});

      if(rs.deletedObjs?.length) {
        showToast("Restoring rows...");
        try {
          const sorted = [...rs.deletedObjs].sort((a,b)=>a.rowIdx-b.rowIdx);
          for(const del of sorted) {
            const refTask = rs.tasks[del.rowIdx - 2];
            const fd = new FormData();
            fd.append("group_id", groupId);
            fd.append("insert_position", "below");
            if(refTask?._id) fd.append("reference_task_id", refTask._id);
            try {
              const addRes = await axiosInstance.post("/task_assignment/todo_group/task/add", fd,
                { headers:{"Content-Type":"multipart/form-data"} });
              const newId = addRes.data?.task?._id;
              if(newId && Object.keys(del.gridRow).length) {
                const updates = Object.entries(del.gridRow)
                  .filter(([ci,val])=> val!=="" && cols[+ci])
                  .map(([ci,val])=>({ task_id:newId, col_key:cols[+ci], value:String(val) }));
                if(updates.length)
                  await axiosInstance.post("/task_assignment/todo_group/task/bulk_cell_update",
                    { group_id:groupId, updates });
              }
            } catch {}
          }
          const reload = await axiosInstance.get(`/task_assignment/todo_group/tasks/${groupId}`);
          if(reload.data?.success && reload.data.tasks) setTasks(reload.data.tasks);
          showToast("Undo complete!");
        } catch {
          showToast("Rows restored visually. Refresh to fully sync.","warning");
        }
      }
    }
  },[history,grid,groupId,cols,showToast]);

  const doRedo = useCallback(() => {
    if(!future.length) return;
    const next = future[0];
    setHistory(h=>[...h,{
      grid:        JSON.parse(JSON.stringify(grid)),
      mergedCells: JSON.parse(JSON.stringify(mergedCellsRef.current)),
      cellFormat:  JSON.parse(JSON.stringify(cellFormatRef.current)),
    }]);
    setFuture(f=>f.slice(1));
    setGrid(next.grid);
    setMergedCells(next.mergedCells || []);
    setCellFormat(next.cellFormat  || {});
  },[future,grid]);

  const startResize = useCallback((e, ci) => {
    e.preventDefault();
    const startX = e.pageX;
    const startW = colW[ci];
    const colEl = tableRef.current
      ? tableRef.current.querySelectorAll("colgroup col")[ci + 1]
      : null;

    const onMove = me => {
      const nw = Math.max(60, startW + (me.pageX - startX));
      if (colEl) { colEl.style.width = nw + "px"; colEl.style.minWidth = nw + "px"; }
    };
    const onUp = me => {
      const nw = Math.max(60, startW + (me.pageX - startX));
      setColW(prev => prev.map((w, i) => i === ci ? nw : w));
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup",   onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup",   onUp);
  }, [colW]);

  const startRowResize = useCallback((e, r, trEl) => {
    e.preventDefault();
    e.stopPropagation();
    const startY = e.pageY;
    const startH = rowH[r] || 26;
    const onMove = me => {
      const nh = Math.max(20, startH + (me.pageY - startY));
      if (trEl) trEl.style.height = nh + "px";
    };
    const onUp = me => {
      const nh = Math.max(20, startH + (me.pageY - startY));
      setRowH(prev => ({ ...prev, [r]: nh }));
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup",   onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup",   onUp);
  }, [rowH]);

  // Corner ▲ select-all → right-click → wipe every row and column; the sheet then shows its empty panel.
  const deleteEntireSheet = useCallback(async () => {
    setCtx(null);
    const taskIds = tasks.map(t=>t?._id).filter(Boolean);
    const colKeys = cols.filter(k=>!String(k).startsWith("__tmp_"));
    if(!window.confirm(`Delete ALL ${tasks.length} row(s) and ${cols.length} column(s) with all their data? This cannot be undone.`)) return;
    markRows(taskIds, true);
    markCols(colKeys, true);
    const endOp = beginOp("Deleting all rows and columns…");
    try {
      if(taskIds.length) {
        const r1 = await axiosInstance.post("/task_assignment/todo_group/task/delete", { group_id:groupId, task_ids:taskIds });
        if(!r1.data?.success) throw new Error(r1.data?.error || "Could not delete rows");
      }
      if(colKeys.length) {
        const fd = new FormData();
        fd.append("group_id", groupId);
        colKeys.forEach(k=>fd.append("col_keys[]",k));
        const r2 = await axiosInstance.post("/task_assignment/todo_group/column/delete", fd,
          { headers:{"Content-Type":"multipart/form-data"} });
        if(!r2.data?.success) throw new Error(r2.data?.error || "Could not delete columns");
      }
      showToast("All rows and columns deleted");
      endOp(true, "✓ All rows and columns deleted");
      logActivity(`${myName} deleted all ${taskIds.length} rows and ${colKeys.length} columns`);
    } catch(err) {
      showToast(err?.message || "Server error","error");
      endOp(false, err?.message || "Server error");
      markRows(taskIds, false);
      markCols(colKeys, false);
    } finally {
      onReload?.();
    }
  },[tasks,cols,groupId,showToast,logActivity,myName,onReload,beginOp,markRows,markCols]);

  const deleteAllCtxItems = useCallback(() => (
    canWrite && isAllSelected()
      ? [{ type:"sep" }, { icon:"🗑️", label:"Delete All Rows & Columns", danger:true, onClick:deleteEntireSheet }]
      : []
  ),[canWrite,isAllSelected,deleteEntireSheet]);

  const showCellCtx = useCallback((e,r,c) => {
    e.preventDefault();
    const colKey = cols[c];
    const curCellBg  = cellBg[`${r}-${c}`]||"none";
    const curCellTx  = cellTx[`${r}-${c}`]||"none";
    const isComplete = rowComplete[r];

    const s = selRange();
    const selRows = s && s.r1>=1 && s.r2>=1 && r>=s.r1 && r<=s.r2
      ? Array.from({length:s.r2-s.r1+1},(_,i)=>s.r1+i) : [r];
    const selCols = s && c>=s.c1 && c<=s.c2
      ? Array.from({length:s.c2-s.c1+1},(_,i)=>s.c1+i) : [c];

    let pos = {x:e.clientX, y:e.clientY};
    if(pos.x+240>window.innerWidth) pos.x=window.innerWidth-244;
    if(pos.y+450>window.innerHeight) pos.y=window.innerHeight-460;

    const selCellCount = s ? (s.r2-s.r1+1)*(s.c2-s.c1+1) : 1;

    const doClearCells = () => {
      if(!canWrite) return;
      const rng = s || { r1:r, r2:r, c1:c, c2:c };
      pushHistory(grid);
      const newGrid = {...grid};
      const updates = [];
      for(let rr=rng.r1; rr<=rng.r2; rr++) for(let cc=rng.c1; cc<=rng.c2; cc++) {
        if(rowComplete[rr]) continue;
        if(!newGrid[rr]) newGrid[rr]={};
        newGrid[rr][cc]="";
        const t=tasks[rr-1]; const col=cols[cc];
        if(t?._id && col) updates.push({task_id:t._id, col_key:col, value:""});
      }
      setGrid(newGrid);
      const rangeLabel = s
        ? `${cellLabel(rng.r1,rng.c1)}–${cellLabel(rng.r2,rng.c2)}`
        : cellLabel(rng.r1, rng.c1);
      logActivity(`${myName} cleared ${selCellCount > 1 ? selCellCount+" cells" : "cell"} ${rangeLabel}`);
      if(updates.length) axiosInstance.post("/task_assignment/todo_group/task/bulk_cell_update",
        {group_id:groupId, updates}).catch(()=>{});
      setCtx(null);
    };

    const items = [
      { type:"sub", icon:"🎨", label:"Cell Background",
        children:<ColorGrid onPick={color=>{applyCellBg(r,c,color);setCtx(null);}} current={curCellBg}/> },
      { type:"sub", icon:"🖊️", label:"Cell Text Color",
        children:<TextColorGrid onPick={color=>{applyCellTx(r,c,color);setCtx(null);}} current={curCellTx}/> },
      { type:"sep" },
      ...(canWrite ? [
        selCellCount > 1
          ? { icon:"🧹", label:`Clear ${selCellCount} Cells`, onClick: doClearCells }
          : { icon:"🧹", label:"Clear Cell", onClick: doClearCells },
        { type:"sep" },
        { type:"sub", icon:"⬆️", label:"Insert Row Above",
          children:<BulkInsertMenu label="Row" onInsert={n=>{setCtx(null);bulkInsertRows(r,"above",n);}}/> },
        { type:"sub", icon:"⬇️", label:"Insert Row Below",
          children:<BulkInsertMenu label="Row" onInsert={n=>{setCtx(null);bulkInsertRows(r,"below",n);}}/> },
        { type:"sep" },
        { type:"sub", icon:"⬅️", label:"Insert Column Left",
          children:<BulkInsertMenu label="Column" onInsert={n=>{setCtx(null);bulkInsertCols(c,"left",n);}}/> },
        { type:"sub", icon:"➡️", label:"Insert Column Right",
          children:<BulkInsertMenu label="Column" onInsert={n=>{setCtx(null);bulkInsertCols(c,"right",n);}}/> },
        { type:"sep" },
        ...(isComplete
          ? [{ icon:"↩️", label:"Reopen Row", onClick:()=>undoComplete(r) }]
          : [{ icon:"✅", label:"Mark as Done",  onClick:()=>markComplete(r) }]
        ),
        { type:"sep" },
        selRows.length>1
          ? { icon:"🗑️", label:`Delete ${selRows.length} Rows`, danger:true, onClick:()=>bulkDeleteRows(selRows) }
          : { icon:"🗑️", label:"Delete Row", danger:true, onClick:()=>deleteRow(r) },
        selCols.length>1
          ? { icon:"🗑️", label:`Delete ${selCols.length} Columns`, danger:true, onClick:()=>bulkDeleteColumns(selCols) }
          : null,
      ].filter(Boolean) : []),
    ];
    items.push(...deleteAllCtxItems());
    setCtx({ pos, items });
  },[deleteAllCtxItems,cols,cellBg,cellTx,rowComplete,canWrite,grid,tasks,groupId,pushHistory,applyCellBg,applyCellTx,insertRow,bulkInsertRows,bulkInsertCols,undoComplete,markComplete,deleteRow,bulkDeleteRows,bulkDeleteColumns,addColumn,selRange,showToast,logActivity,myName]);

  const showRowCtx = useCallback((e,r) => {
    e.preventDefault();
    const curBg = rowBg[r]||"none";
    const curTx = rowTx[r]||"none";
    const isComplete = rowComplete[r];

    const s = selRange();
    const selRows = s && s.r1>=1 && r>=s.r1 && r<=s.r2
      ? Array.from({length:s.r2-s.r1+1},(_,i)=>s.r1+i) : [r];

    let pos={x:e.clientX,y:e.clientY};
    if(pos.x+240>window.innerWidth) pos.x=window.innerWidth-244;
    if(pos.y+450>window.innerHeight) pos.y=window.innerHeight-460;

    const items=[
      ...(canWrite ? [
        { type:"sub", icon:"⬆️", label:"Insert Row Above",
          children:<BulkInsertMenu label="Row" onInsert={n=>{setCtx(null);bulkInsertRows(r,"above",n);}}/> },
        { type:"sub", icon:"⬇️", label:"Insert Row Below",
          children:<BulkInsertMenu label="Row" onInsert={n=>{setCtx(null);bulkInsertRows(r,"below",n);}}/> },
        { type:"sep" },
        ...(isComplete
          ? [{ icon:"↩️", label:"Reopen Row", onClick:()=>undoComplete(r) }]
          : [{ icon:"✅", label:"Mark as Done",  onClick:()=>markComplete(r) }]
        ),
        { type:"sep" },
      ] : []),
      { type:"sub", icon:"🎨", label:"Row Background",
        children:<ColorGrid onPick={color=>{applyRowBg(r,color);setCtx(null);}} current={curBg}/> },
      { type:"sub", icon:"🖊️", label:"Row Text Color",
        children:<TextColorGrid onPick={color=>{applyRowTx(r,color);setCtx(null);}} current={curTx}/> },
      ...(canWrite ? [
        { type:"sep" },
        selRows.length>1
          ? { icon:"🗑️", label:`Delete ${selRows.length} Rows`, danger:true, onClick:()=>bulkDeleteRows(selRows) }
          : { icon:"🗑️", label:"Delete Row", danger:true, onClick:()=>deleteRow(r) },
      ] : []),
    ];
    items.push(...deleteAllCtxItems());
    setCtx({ pos, items });
  },[deleteAllCtxItems,rowBg,rowTx,rowComplete,canWrite,insertRow,undoComplete,markComplete,applyRowBg,applyRowTx,deleteRow,bulkDeleteRows,selRange]);

  const showColCtx = useCallback((e,ci) => {
    e.preventDefault();
    const colKey = cols[ci];
    const curBg  = colBg[colKey]||"none";
    const curTx  = colTx[colKey]||"none";

    const s = selRange();
    const selCols = s && ci>=s.c1 && ci<=s.c2
      ? Array.from({length:s.c2-s.c1+1},(_,i)=>s.c1+i) : [ci];

    let pos={x:e.clientX,y:e.clientY};
    if(pos.x+240>window.innerWidth) pos.x=window.innerWidth-244;
    if(pos.y+350>window.innerHeight) pos.y=window.innerHeight-360;

    const items=[
      ...(canWrite ? [
        { icon:"✏️",  label:"Rename Column", onClick:()=>{
            const cur = colDisplayNames[colKey]||colKey;
            const nr  = window.prompt("New column name:", cur);
            if(!nr?.trim()) return;
            const trimmed = nr.trim();
            setGrid(g => ({...g, 0:{...(g[0]||{}),[ci]:trimmed}}));
            logActivity(`${myName} renamed column "${cur}" to "${trimmed}"`);
            axiosInstance.post("/task_assignment/todo_group/column/rename",
              {group_id:groupId, col_key:colKey, new_name:trimmed})
              .catch(()=>setGrid(g=>({...g,0:{...(g[0]||{}),[ci]:cur}})));
          }},
        { type:"sub", icon:"⬅️", label:"Insert Column Left",
          children:<BulkInsertMenu label="Column" onInsert={n=>{setCtx(null);bulkInsertCols(ci,"left",n);}}/> },
        { type:"sub", icon:"➡️", label:"Insert Column Right",
          children:<BulkInsertMenu label="Column" onInsert={n=>{setCtx(null);bulkInsertCols(ci,"right",n);}}/> },
        { icon:"➕", label:"Add Row",               onClick:()=>insertRow(tasks.length, "below") },
        { type:"sep" },
      ] : []),
      { type:"sub", icon:"🎨", label:"Column Background",
        children:<ColorGrid onPick={color=>{applyColBg(colKey,color);setCtx(null);}} current={curBg}/> },
      { type:"sub", icon:"🖊️", label:"Column Text Color",
        children:<TextColorGrid onPick={color=>{applyColTx(colKey,color);setCtx(null);}} current={curTx}/> },
      ...(canWrite ? [
        { type:"sep" },
        selCols.length>1
          ? { icon:"🗑️", label:`Delete ${selCols.length} Columns`, danger:true, onClick:()=>bulkDeleteColumns(selCols) }
          : { icon:"🗑️", label:"Delete Column", danger:true, onClick:()=>deleteColumn(colKey) },
      ] : []),
    ];
    items.push(...deleteAllCtxItems());
    setCtx({ pos, items });
  },[deleteAllCtxItems,cols,colBg,colTx,colDisplayNames,canWrite,addColumn,insertRow,tasks,applyColBg,applyColTx,deleteColumn,bulkDeleteColumns,selRange,groupId,showToast,logActivity,myName]);

  useEffect(() => {
    const onKey = (e) => {
      if((e.ctrlKey||e.metaKey) && e.key==="f") {
        e.preventDefault();
        setFindOpen(true);
        return;
      }

      const tag = document.activeElement?.tagName;
      if(tag==="INPUT"&&!document.activeElement?.classList.contains("xl-edit-input")&&
         document.activeElement?.id!=="xl-formula-input") return;
      if(!activeCell && !editCell) return;

      const {r,c} = activeCell||{r:0,c:0};

      if(e.key==="F2" && !editCell) { e.preventDefault(); startEdit(r,c); return; }
      if(e.key==="Escape") {
        if(findOpen) { setFindOpen(false); return; }
        if(editCell) cancelEdit(); else { setCopyRange(null); setIsCut(false); }
        return;
      }
      if(e.key==="Enter") { e.preventDefault(); if(editCell) commitEdit("down"); else startEdit(r,c); return; }
      if(e.key==="Tab") {
        e.preventDefault();
        if(editCell) commitEdit(e.shiftKey?"left":"right");
        else navigate(r,c,e.shiftKey?"left":"right");
        return;
      }
      if(["ArrowUp","ArrowDown","ArrowLeft","ArrowRight"].includes(e.key)) {
        if(editCell) return;
        e.preventDefault();
        const dir={ArrowUp:"up",ArrowDown:"down",ArrowLeft:"left",ArrowRight:"right"}[e.key];
        if(e.shiftKey && selStart && selEnd) {
          let nr=selEnd.r, nc=selEnd.c;
          if(dir==="down")  nr=Math.min(nr+1,numRows-1);
          if(dir==="up")    nr=Math.max(nr-1,0);
          if(dir==="right") nc=Math.min(nc+1,numCols-1);
          if(dir==="left")  nc=Math.max(nc-1,0);
          setSelEnd({r:nr,c:nc});
        } else navigate(r,c,dir);
        return;
      }
      if((e.key==="Delete"||e.key==="Backspace") && !editCell) {
        if(!canWrite) return;
        e.preventDefault();
        const s=selRange(); if(!s) return;
        pushHistory(grid);
        const newGrid={...grid};
        const updates=[];
        for(let rr=s.r1;rr<=s.r2;rr++) for(let cc=s.c1;cc<=s.c2;cc++){
          if(rowComplete[rr]) continue;
          if(!newGrid[rr]) newGrid[rr]={};
          newGrid[rr][cc]="";
          const t=tasks[rr-1]; const col=cols[cc];
          if(t?._id&&col) updates.push({task_id:t._id,col_key:col,value:""});
        }
        setGrid(newGrid);
        if(updates.length) axiosInstance.post("/task_assignment/todo_group/task/bulk_cell_update",
          {group_id:groupId,updates}).catch(()=>{});
        return;
      }
      if(e.ctrlKey||e.metaKey) {
        if(e.key==="a") { if(editCell) return; e.preventDefault(); selectAll(); return; }
        if(e.key==="c") { if(editCell) return; e.preventDefault(); doCopy(false); return; }
        if(e.key==="x") { if(editCell||!canWrite) return; e.preventDefault(); doCopy(true); return; }
        if(e.key==="v") { if(editCell) return; e.preventDefault(); doPaste(); return; }
        if(e.key==="z") { e.preventDefault(); doUndo(); return; }
        if(e.key==="y") { e.preventDefault(); doRedo(); return; }
        if(e.key==="h") { e.preventDefault(); setFindOpen(true); return; }
        if(e.key==="b") { if(editCell) return; e.preventDefault(); applyFormat({bold:!activeFmt.bold}); return; }
        if(e.key==="i") { if(editCell) return; e.preventDefault(); applyFormat({italic:!activeFmt.italic}); return; }
        if(e.key==="u") { if(editCell) return; e.preventDefault(); applyFormat({underline:!activeFmt.underline}); return; }
      }
      if(!editCell && !e.ctrlKey && !e.metaKey && e.key.length===1) {
        startEdit(r,c,e.key);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  },[activeCell,editCell,selStart,selEnd,canWrite,grid,tasks,cols,numRows,numCols,
     groupId,rowComplete,startEdit,cancelEdit,commitEdit,navigate,selectAll,
     selRange,pushHistory,doCopy,doPaste,doUndo,doRedo,findOpen,setFindOpen,
     applyFormat,activeFmt]);

  const fbarVal = editCell
    ? editVal
    : activeCell
      ? (grid[activeCell.r]?.[activeCell.c] ?? "")
      : "";

  const getCellStyle = (r,c) => {
    const colKey = cols[c];
    const key=`${r}-${c}`;
    let bg="";
    if(r>0 && rowComplete[r]) bg="#e5e7eb";
    else if(r===0) bg="#fef9c3";
    else if(cellBg[key]) bg=ROW_COLORS[cellBg[key]]||cellBg[key];
    else if(colBg[colKey]) bg=ROW_COLORS[colBg[colKey]]||colBg[colKey];
    else if(rowBg[r]) bg=ROW_COLORS[rowBg[r]]||"";
    const tx = (cellTx[key] && (TEXT_COLORS_HEX[cellTx[key]]||cellTx[key]))
            || (colTx[colKey] && (TEXT_COLORS_HEX[colTx[colKey]]||colTx[colKey]))
            || (rowTx[r] ? TEXT_COLORS_HEX[rowTx[r]] : "");
    const active    = isActive(r,c);
    const sel       = isSelected(r,c);
    const cut       = isCutCell(r,c);
    const isMatch   = matchSet.has(`${r},${c}`);
    const isCurMatch = isMatch && matchResults[matchIdx]?.r===r && matchResults[matchIdx]?.c===c;
    let background = sel&&!active ? "#cce5ff" : bg||"#fff";
    if (isMatch)    background = "#fff3a3";
    if (isCurMatch) background = "#ffa500";
    const fmt = cellFormat[key] || {};
    if (fmt.fillColor) background = fmt.fillColor;
    return {
      background,
      color: fmt.fontColor || tx || "#212529",
      outline: active && !editCell ? "2px solid #1f6feb" : cut ? "2px dashed #1f6feb" : "none",
      outlineOffset: -2,
      fontWeight: r===0 ? 700 : (fmt.bold ? "bold" : 400),
      fontStyle: fmt.italic ? "italic" : undefined,
      fontFamily: fmt.fontFamily || undefined,
      fontSize: fmt.fontSize ? `${fmt.fontSize}px` : undefined,
      textAlign: fmt.align || undefined,
      cursor: canWrite&&!rowComplete[r] ? "cell" : "default",
    };
  };

  /* ════ RENDER ════ */
  return (
    <div className="flex flex-col h-full overflow-hidden">

      {/* Toolbar — single row: group info LEFT, actions RIGHT */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-[#e2e8f0] shrink-0 flex-wrap gap-1.5"
        style={{ background:`linear-gradient(135deg,${groupColor||"#7c3aed"}12,${groupColor||"#7c3aed"}04)` }}>
        <div className="flex items-center gap-2.5">
          {/* Group icon + name */}
          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-black shrink-0"
            style={{ background:groupColor||"#7c3aed" }}>
            {groupName?.charAt(0)||"G"}
          </div>
          <div className="leading-tight">
            <div className="text-[13px] font-extrabold text-[#1e293b]">{groupName}</div>
            <div className="text-[10px] text-[#94a3b8]">
              {members.length} member{members.length!==1?"s":""}
            </div>
          </div>
          {/* Divider */}
          <div className="w-px h-7 bg-[#e2e8f0] mx-1.5 shrink-0"/>
          {/* Avatars */}
          <div className="flex items-center">
            {members.slice(0,5).map((m,i)=>(
              <div key={m.id||i} title={m.name||""}
                className="rounded-full border-2 border-white leading-none shrink-0"
                style={{ marginLeft:i===0?0:-8, zIndex:10-i }}>
                <PhotoAvatar name={m.name||""} userId={m.id} size={26}/>
              </div>
            ))}
          </div>
          {isAdmin && <span className="text-[10px] bg-[#fef9c3] text-[#854d0e] border border-[#fde68a] rounded px-1.5 py-px font-bold">👑 ADMIN</span>}
          {!canWrite && <span className="text-[10px] bg-[#fee2e2] text-[#991b1b] border border-[#fecaca] rounded px-1.5 py-px font-bold">🔒 READ ONLY</span>}
          {saving && <span className="text-[10px] text-[#94a3b8] italic">Saving…</span>}
        </div>
        <div className="flex items-center gap-1.5">

          {/* ⓘ Help icon */}
          <div className="relative"
            onMouseEnter={() => setShortcutOpen(true)}
            onMouseLeave={() => setShortcutOpen(false)}>
            <div className={`w-[22px] h-[22px] rounded-full flex items-center justify-center text-[15px] font-black cursor-default italic transition-colors select-none border-[1.5px] border-[#c4b5fd] leading-none ${
              shortcutOpen ? "bg-[#7C3AED] text-white" : "bg-[#f3f0ff] text-[#7C3AED]"
            }`} style={{ fontFamily:"Georgia,'Times New Roman',serif", letterSpacing:0 }}>
              i
            </div>
            {shortcutOpen && (
              <div className="absolute top-[calc(100%+8px)] right-0 z-[9999] bg-white border border-[#e2e8f0] rounded-[10px] shadow-[0_8px_32px_rgba(0,0,0,0.13)] pt-3 pb-2 min-w-[300px] text-[11px] select-none">
                <div className="px-3.5 pb-2.5 border-b border-[#f1f5f9] flex items-center gap-2">
                  <span className="w-[18px] h-[18px] rounded-full bg-[#7C3AED] text-white flex items-center justify-center text-[13px] font-black italic shrink-0 leading-none"
                    style={{ fontFamily:"Georgia,'Times New Roman',serif" }}>i</span>
                  <span className="font-bold text-xs text-[#1e293b]">Keyboard Shortcuts</span>
                </div>
                <div className="grid pt-2 pb-1 px-3.5 gap-x-4 gap-y-[3px] grid-cols-2">
                  {[
                    ["F2",              "Edit cell"],
                    ["Escape",          "Cancel edit"],
                    ["Enter",           "Confirm & move down"],
                    ["Tab / Shift+Tab", "Move right / left"],
                    ["Arrow Keys",      "Navigate cells"],
                    ["Shift + Arrow",   "Extend selection"],
                    ["Del / Backspace", "Clear cell"],
                    ["Ctrl+C/X/V",      "Copy / Cut / Paste"],
                    ["Ctrl+Z / Ctrl+Y", "Undo / Redo"],
                    ["Ctrl+A",          "Select all"],
                    ["Ctrl+F",          "Find & Replace"],
                  ].map(([key, desc]) => (
                    <div key={key} className="flex items-center gap-1.5 py-[3px]">
                      <span className="font-mono bg-[#f3f0ff] border border-[#ddd6fe] rounded px-1.5 py-px text-[10px] text-[#7C3AED] whitespace-nowrap shrink-0 font-semibold">
                        {key}
                      </span>
                      <span className="text-[#64748b] text-[10px] leading-[1.3]">{desc}</span>
                    </div>
                  ))}
                </div>
                <div className="absolute -top-1.5 right-[7px] w-2.5 h-2.5 bg-white border border-[#e2e8f0] rotate-45 border-b-0 border-r-0"/>
              </div>
            )}
          </div>

          <button onClick={()=>setActivityOpen(true)}
            className="flex items-center gap-1 px-2.5 py-1 border border-[#e2e8f0] rounded-[7px] bg-[#f8fafc] text-[11px] cursor-pointer font-semibold text-[#374151]">
            <Activity size={11}/> Activity
          </button>
          <button onClick={onReload}
            className="flex items-center gap-1 px-2.5 py-1 border border-[#e2e8f0] rounded-[7px] bg-[#f8fafc] text-[11px] cursor-pointer font-semibold text-[#374151]">
            <RefreshCw size={11}/> Refresh
          </button>
          {isAdmin && (
            <div ref={exportRef} className="relative">
              <button onClick={() => setExportOpen(o => !o)}
                className="flex items-center gap-1 px-2.5 py-1 border border-[#a7f3d0] rounded-[7px] bg-[#ecfdf5] text-[11px] cursor-pointer font-bold text-[#059669]">
                <Download size={11}/> Export ▾
              </button>
              {exportOpen && (
                <div className="absolute right-0 top-[calc(100%+4px)] z-[9999] bg-white border border-[#d0d7de] rounded-lg shadow-[0_8px_24px_rgba(0,0,0,0.14)] min-w-[160px] py-1 overflow-hidden">
                  <div onClick={() => { exportCSV(); setExportOpen(false); }}
                    className="flex items-center gap-2 px-4 py-2 cursor-pointer text-xs font-semibold text-[#059669]"
                    onMouseEnter={e => e.currentTarget.style.background="#ecfdf5"}
                    onMouseLeave={e => e.currentTarget.style.background=""}>
                    <Download size={13}/> Export as CSV
                  </div>
                  <div onClick={() => { exportXLSX(); setExportOpen(false); }}
                    className="flex items-center gap-2 px-4 py-2 cursor-pointer text-xs font-semibold text-[#2563eb]"
                    onMouseEnter={e => e.currentTarget.style.background="#eff6ff"}
                    onMouseLeave={e => e.currentTarget.style.background=""}>
                    <Download size={13}/> Export as XLSX
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Read-only banner */}
      {!canWrite && (
        <div className="flex items-center gap-1.5 px-3 py-[5px] bg-[#fef3c7] border-b border-[#fde68a] shrink-0 text-xs text-[#92400e]">
          <Lock size={11}/> <span><strong>Read Only Mode</strong> — You do not have write permission. Contact the group  admin.</span>
        </div>
      )}

      {/* ── Font Formatting Toolbar (single row, scrolls horizontally) ── */}
      {(()=>{
        const B  = "flex items-center justify-center gap-[3px] px-1.5 text-xs cursor-pointer border border-[#d0d7de] rounded bg-white text-[#24292f] h-6 shrink-0";
        const BA = "flex items-center justify-center gap-[3px] px-1.5 text-xs cursor-pointer border border-[#3b82f6] rounded bg-[#dbeafe] text-[#1d4ed8] h-6 shrink-0";
        const D  = <div className="w-px h-5 bg-[#d0d7de] mx-[3px] shrink-0"/>;
        const curSize = activeFmt.fontSize || 13;
        const sizeIdx = FONT_SIZES.findIndex(s => s >= curSize);
        return (
          <div className="flex items-center gap-[3px] px-2 py-[3px] border-b border-[#d0d7de] bg-[#f8fafc] shrink-0 overflow-x-auto overflow-y-hidden whitespace-nowrap">

            <select value={activeFmt.fontFamily||"Default"}
              onChange={e=>applyFormat({fontFamily:e.target.value==="Default"?undefined:e.target.value})}
              title="Font family"
              className="text-xs px-1 border border-[#d0d7de] rounded bg-white cursor-pointer h-6 min-w-[130px] shrink-0">
              {FONT_FAMILIES.map(f=><option key={f} value={f}>{f}</option>)}
            </select>

            <select value={curSize}
              onChange={e=>applyFormat({fontSize:Number(e.target.value)})}
              title="Font size"
              className="text-xs px-1 border border-[#d0d7de] rounded bg-white cursor-pointer h-6 w-[52px] shrink-0">
              {FONT_SIZES.map(s=><option key={s} value={s}>{s}</option>)}
            </select>

            <button onClick={()=>applyFormat({fontSize:FONT_SIZES[Math.min(sizeIdx+1,FONT_SIZES.length-1)]})} title="Increase font size" className={B}>
              <span className="text-[13px] font-bold">A</span><span className="text-[8px] font-bold">▲</span>
            </button>
            <button onClick={()=>applyFormat({fontSize:FONT_SIZES[Math.max(sizeIdx<=0?0:sizeIdx-1,0)]})} title="Decrease font size" className={B}>
              <span className="text-[13px] font-bold">A</span><span className="text-[8px] font-bold">▼</span>
            </button>

            {D}

            <button onClick={()=>applyFormat({bold:!activeFmt.bold})} title="Bold (Ctrl+B)" className={activeFmt.bold?BA:B}><Bold size={13}/></button>
            <button onClick={()=>applyFormat({italic:!activeFmt.italic})} title="Italic (Ctrl+I)" className={activeFmt.italic?BA:B}><Italic size={13}/></button>
            <button onClick={()=>applyFormat({underline:!activeFmt.underline})} title="Underline (Ctrl+U)" className={activeFmt.underline?BA:B}><Underline size={13}/></button>

            {D}

            <button onClick={()=>applyFormat({align:activeFmt.align==="left"?undefined:"left"})} title="Align left" className={activeFmt.align==="left"?BA:B}><AlignLeft size={13}/></button>
            <button onClick={()=>applyFormat({align:activeFmt.align==="center"?undefined:"center"})} title="Align center" className={activeFmt.align==="center"?BA:B}><AlignCenter size={13}/></button>
            <button onClick={()=>applyFormat({align:activeFmt.align==="right"?undefined:"right"})} title="Align right" className={activeFmt.align==="right"?BA:B}><AlignRight size={13}/></button>

            {D}

            <button onClick={()=>applyFormat({indent:Math.max((activeFmt.indent||0)-1,0)})} title="Decrease indent" className={B}>
              <span className="text-[13px]">⇤</span><span className="text-[11px]">≡</span>
            </button>
            <button onClick={()=>applyFormat({indent:(activeFmt.indent||0)+1})} title="Increase indent" className={B}>
              <span className="text-[11px]">≡</span><span className="text-[13px]">⇥</span>
            </button>

            {D}

            <div ref={fillColorRef} className="relative shrink-0">
              <button onClick={e=>{const r=e.currentTarget.getBoundingClientRect();setFillColorPos({top:r.bottom+4,left:r.left});setFillColorOpen(o=>!o);}} title="Fill Color" className={fillColorOpen?BA:B}>
                <PaintBucket size={13}/>
                <span className="block w-[13px] h-[3px] ml-px rounded-[1px]" style={{ background:activeFmt.fillColor||"#fbbf24" }}/>
              </button>
              {fillColorOpen&&(
                <div className="fixed z-[99999] bg-white border border-[#d0d7de] rounded-md shadow-[0_4px_16px_rgba(0,0,0,0.15)] p-1.5"
                  style={{ top:fillColorPos.top, left:fillColorPos.left }}>
                  <div className="text-[11px] text-[#6c757d] mb-1 font-semibold">Fill Color</div>
                  <ColorGrid onPick={name=>{applyFormat({fillColor:name==="none"?undefined:ROW_COLORS[name]});setFillColorOpen(false);}} current="none"/>
                </div>
              )}
            </div>

            <div ref={fontColorRef} className="relative shrink-0">
              <button onClick={e=>{const r=e.currentTarget.getBoundingClientRect();setFontColorPos({top:r.bottom+4,left:r.left});setFontColorOpen(o=>!o);}} title="Font Color" className={fontColorOpen?BA:B}>
                <Type size={13}/>
                <span className="block w-[13px] h-[3px] ml-px rounded-[1px]" style={{ background:activeFmt.fontColor||"#dc2626" }}/>
              </button>
              {fontColorOpen&&(
                <div className="fixed z-[99999] bg-white border border-[#d0d7de] rounded-md shadow-[0_4px_16px_rgba(0,0,0,0.15)] p-1.5"
                  style={{ top:fontColorPos.top, left:fontColorPos.left }}>
                  <div className="text-[11px] text-[#6c757d] mb-1 font-semibold">Font Color</div>
                  <TextColorGrid onPick={name=>{applyFormat({fontColor:name==="none"?undefined:TEXT_COLORS_HEX[name]});setFontColorOpen(false);}} current="none"/>
                </div>
              )}
            </div>

          </div>
        );
      })()}

      {/* ── Excel-like Toolbar Banner ── */}
      <div className="flex items-center gap-[3px] px-2 py-[3px] border-b border-[#d0d7de] bg-[#f0f4f8] shrink-0 flex-wrap">

        {/* AutoSum dropdown */}
        <div ref={autoSumRef} className="relative">
          <button onClick={() => setAutoSumOpen(o => !o)}
            title="Apply a formula to the selected column"
            className={`flex items-center gap-1 px-2.5 py-[3px] text-xs cursor-pointer font-semibold border border-[#d0d7de] rounded text-[#24292f] leading-[1.4] ${
              autoSumOpen ? "bg-[#dbeafe]" : "bg-white"
            }`}>
            <span className="text-sm font-bold">Σ</span> AutoSum
            <svg width="8" height="8" viewBox="0 0 8 8" className="shrink-0">
              <path d="M1 2l3 4 3-4" fill="none" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
          </button>
          {autoSumOpen && (
            <div className="absolute top-[calc(100%+2px)] left-0 z-[9999] bg-white border border-[#d0d7de] rounded-md shadow-[0_4px_16px_rgba(0,0,0,0.15)] py-1 min-w-[170px]">
              {[{label:"Sum",icon:"Σ"},{label:"Average",icon:"x̄"},
                {label:"Count Numbers",icon:"#"},{label:"Max",icon:"↑"},{label:"Min",icon:"↓"}
              ].map(({label,icon}) => (
                <div key={label} onClick={() => computeAutoSum(label)}
                  onMouseEnter={e => e.currentTarget.style.background="#f0f6ff"}
                  onMouseLeave={e => e.currentTarget.style.background=""}
                  className="flex items-center gap-2 px-3.5 py-[7px] cursor-pointer text-[13px] text-[#24292f] whitespace-nowrap">
                  <span className="w-4 text-center font-bold text-[#6c757d] text-sm shrink-0">{icon}</span>
                  {label}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="w-px h-5 bg-[#d0d7de] mx-0.5 shrink-0"/>

        {/* Filter */}
        <button
          onClick={e => {
            if (!activeCell) { showToast("Select a cell first","warning"); return; }
            const rect = e.currentTarget.getBoundingClientRect();
            setFilterDrop({ ci: activeCell.c, pos: { x: rect.left, y: rect.bottom + 4 } });
          }}
          title="Filter selected column"
          className={`flex items-center gap-1 px-2.5 py-[3px] text-xs cursor-pointer leading-[1.4] border border-[#d0d7de] rounded text-[#24292f] ${
            hasActiveFilter ? "bg-[#fef3c7]" : "bg-white"
          }`}>
          <Filter size={12}/> Filter
        </button>

        {/* Clear Filter (only when active) */}
        {hasActiveFilter && (
          <button onClick={clearAllFilters}
            title="Clear all filters and sorts"
            className="flex items-center gap-1 px-2.5 py-[3px] text-xs cursor-pointer leading-[1.4] bg-white border border-[#fca5a5] rounded text-[#dc2626]">
            <X size={11}/> Clear Filter
          </button>
        )}

        {/* Sort A→Z */}
        <button
          onClick={() => { if (!activeCell) return; applyFilter({ sort:"asc" }, activeCell.c); logActivity(`${myName} sorted column ${colLetter(activeCell.c)} A → Z`); }}
          title="Sort ascending (A → Z)"
          className="flex items-center gap-1 px-2.5 py-[3px] text-xs cursor-pointer leading-[1.4] bg-white border border-[#d0d7de] rounded text-[#24292f]">
          A↑Z
        </button>

        {/* Sort Z→A */}
        <button
          onClick={() => { if (!activeCell) return; applyFilter({ sort:"desc" }, activeCell.c); logActivity(`${myName} sorted column ${colLetter(activeCell.c)} Z → A`); }}
          title="Sort descending (Z → A)"
          className="flex items-center gap-1 px-2.5 py-[3px] text-xs cursor-pointer leading-[1.4] bg-white border border-[#d0d7de] rounded text-[#24292f]">
          Z↓A
        </button>

        <div className="w-px h-5 bg-[#d0d7de] mx-0.5 shrink-0"/>

        {/* Find & Replace */}
        <button onClick={() => setFindOpen(o => !o)}
          title="Find & Replace (Ctrl+H)"
          className={`flex items-center gap-1 px-2.5 py-[3px] text-xs cursor-pointer leading-[1.4] rounded border ${
            findOpen ? "bg-[#dbeafe] border-[#3b82f6] text-[#1d4ed8]" : "bg-white border-[#d0d7de] text-[#24292f]"
          }`}>
          <Search size={12}/> Find &amp; Replace
        </button>

        <div className="w-px h-5 bg-[#d0d7de] mx-0.5 shrink-0"/>

        {/* Merge & Center */}
        <div ref={mergeRef} className="relative">
          <button onClick={() => setMergeOpen(o => !o)}
            title="Merge & Center cells"
            className={`flex items-center gap-1 px-2.5 py-[3px] text-xs cursor-pointer leading-[1.4] font-semibold border border-[#d0d7de] rounded text-[#24292f] ${
              mergeOpen ? "bg-[#dbeafe]" : "bg-white"
            }`}>
            ⊞ Merge &amp; Center
            <svg width="8" height="8" viewBox="0 0 8 8" className="shrink-0">
              <path d="M1 2l3 4 3-4" fill="none" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
          </button>
          {mergeOpen && (
            <div className="absolute top-[calc(100%+2px)] left-0 z-[9999] bg-white border border-[#d0d7de] rounded-md shadow-[0_4px_16px_rgba(0,0,0,0.15)] py-1 min-w-[180px]">
              {[
                { label:"Merge & Center", fn: () => doMergeCells(true) },
                { label:"Merge Across",   fn: doMergeAcross },
                { label:"Merge Cells",    fn: () => doMergeCells(false) },
                { label:"Unmerge Cells",  fn: doUnmerge },
              ].map(({ label, fn }) => (
                <div key={label} onClick={fn}
                  onMouseEnter={e => e.currentTarget.style.background="#f0f6ff"}
                  onMouseLeave={e => e.currentTarget.style.background=""}
                  className="flex items-center px-3.5 py-[7px] cursor-pointer text-[13px] text-[#24292f] whitespace-nowrap">
                  {label}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Freeze Panes */}
        <div ref={freezeDropRef} className="relative shrink-0">
          <button onClick={()=>setFreezeDropOpen(o=>!o)}
            title="Freeze Panes"
            className={`flex items-center gap-1 px-2.5 py-[3px] text-xs cursor-pointer font-semibold leading-[1.4] rounded border ${
              (freezeTopRow||freezeFirstCol) ? "bg-[#e8f0fe] border-[#3b82f6] text-[#1d4ed8]" : "bg-white border-[#d0d7de] text-[#24292f]"
            }`}>
            ❄ Freeze Panes ▾
          </button>
          {freezeDropOpen && (
            <div className="fixed z-[99999] bg-white border border-[#d0d7de] rounded-md shadow-[0_4px_20px_rgba(0,0,0,0.15)] min-w-[280px]"
              style={{
                top: (() => { const r=freezeDropRef.current?.getBoundingClientRect(); return r ? r.bottom+2 : 80; })(),
                left: (() => { const r=freezeDropRef.current?.getBoundingClientRect(); return r ? r.left : 0; })(),
              }}>
              {[
                { icon:"🔓", label:"Unfreeze Panes",    desc:"Unlock all rows and columns to scroll through the entire worksheet.",
                  active: !freezeTopRow && !freezeFirstCol,
                  onClick:()=>{ setFreezeTopRow(false); setFreezeFirstCol(false); setFreezeDropOpen(false); logActivity(`${myName} unfroze panes`); } },
                { icon:"🔒", label:"Freeze Top Row",    desc:"Keep the top row visible while scrolling through the rest of the worksheet.",
                  active: freezeTopRow,
                  onClick:()=>{ setFreezeTopRow(true); setFreezeFirstCol(false); setFreezeDropOpen(false); logActivity(`${myName} froze the top row`); } },
                { icon:"🔒", label:"Freeze First Column", desc:"Keep the first column visible while scrolling through the rest of the worksheet.",
                  active: freezeFirstCol,
                  onClick:()=>{ setFreezeFirstCol(true); setFreezeTopRow(false); setFreezeDropOpen(false); logActivity(`${myName} froze the first column`); } },
              ].map((item,i)=>(
                <div key={i} onClick={item.onClick}
                  className={`flex items-start gap-2.5 px-3.5 py-2.5 cursor-pointer ${i<2 ? "border-b border-[#f0f0f0]" : ""}`}
                  style={{ background: item.active ? "#e8f0fe":"#fff" }}
                  onMouseEnter={e=>e.currentTarget.style.background=item.active?"#dbeafe":"#f6f8fa"}
                  onMouseLeave={e=>e.currentTarget.style.background=item.active?"#e8f0fe":"#fff"}>
                  <span className="text-lg shrink-0 mt-px">{item.icon}</span>
                  <div>
                    <div className="text-[13px] font-bold" style={{ color: item.active?"#1d4ed8":"#1e293b" }}>{item.label}</div>
                    <div className="text-[11px] text-[#64748b] mt-0.5 leading-[1.4]">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Remove Duplicates */}
        {canWrite && (
          <button onClick={openRemoveDuplicates}
            title="Remove duplicate rows"
            className="flex items-center gap-1 px-2.5 py-[3px] text-xs cursor-pointer font-semibold leading-[1.4] bg-white border border-[#d0d7de] rounded text-[#24292f] shrink-0">
            ⧉ Remove Duplicates
          </button>
        )}

        {/* AutoSum result badge */}
        {autoSumResult && (
          <div className="ml-auto flex items-center gap-1.5 shrink-0">
            <span className="text-xs bg-[#f0fdf4] border border-[#86efac] rounded px-2.5 py-0.5 font-semibold text-[#15803d]">
              {autoSumResult.label}: {typeof autoSumResult.value === "number"
                ? autoSumResult.value.toLocaleString() : autoSumResult.value}
            </span>
            <button onClick={() => setAutoSumResult(null)}
              className="bg-transparent border-none cursor-pointer text-[#94a3b8] p-0.5 flex items-center">
              <X size={11}/>
            </button>
          </div>
        )}
      </div>

      {/* Formula bar */}
      <div className="flex items-center gap-1.5 px-2 py-[3px] border-b border-[#e2e8f0] bg-[#f8fafc] shrink-0">
        <div className="w-[52px] text-center font-bold text-[11px] text-[#6c757d] border border-[#e2e8f0] rounded px-1 py-0.5 bg-white min-w-[52px] shrink-0">
          {activeCell ? cellLabel(activeCell.r,activeCell.c) : "—"}
        </div>
        <span className="text-[#adb5bd] font-bold text-[13px]">fx</span>
        <input id="xl-formula-input" value={fbarVal}
          readOnly={!editCell}
          onChange={e => editCell && setEditVal(e.target.value)}
          className="flex-1 border border-[#e2e8f0] rounded px-1.5 py-0.5 text-xs outline-none bg-white text-[#212529]"/>
      </div>

      {/* Progress bar — visible while any row/column change is waiting for the server */}
      <style>{SHEET_PENDING_CSS}</style>
      <div className="h-[3px] shrink-0 overflow-hidden bg-transparent relative">
        {opsRunning > 0 && (
          <div className="sheet-progress-bar absolute inset-y-0 left-0 w-1/4 bg-[linear-gradient(90deg,transparent,#7c3aed,transparent)]"/>
        )}
      </div>

      {/* Grid */}
      <div ref={wrapRef} className="flex-1 overflow-auto relative"
        onClick={() => setCtx(null)}>
        {/* Empty sheet — every row and/or column was deleted: offer to add some back.
            Portaled to <body> as a fixed popup so the grid's small height never clips it;
            no backdrop, so the circle list stays clickable. */}
        {(tasks.length === 0 || cols.length === 0) && createPortal(
          <div className="fixed inset-0 z-[50] flex items-center justify-center pointer-events-none p-4">
            <div className="pointer-events-auto bg-white border border-[#e2e8f0] rounded-xl shadow-2xl px-6 py-5 text-center w-[380px] max-w-full max-h-[90vh] overflow-y-auto">
              <div className="text-[15px] font-bold text-[#1e293b] mb-1">This sheet is empty</div>
              <div className="text-[12px] text-[#64748b] mb-4">
                {cols.length === 0 ? "How many columns and rows do you need?" : "How many rows do you need?"}
              </div>
              {canWrite ? (
                <>
                <form onSubmit={e => { e.preventDefault(); startSheet(); }} className="flex flex-col gap-3">
                  <div className="flex justify-center gap-3">
                    {cols.length === 0 && (
                      <label className="flex flex-col items-start gap-1">
                        <span className="text-[11px] font-semibold text-[#94a3b8] uppercase tracking-wide">Columns</span>
                        <input type="number" min={1} max={SHEET_MAX_COLS} value={newSheetCols} autoFocus
                          onChange={e => setNewSheetCols(e.target.value)} disabled={startingSheet}
                          className="w-[110px] px-2.5 py-1.5 border border-[#cbd5e1] rounded-md text-[13px] text-[#1e293b] outline-none focus:border-[#6366f1]"/>
                      </label>
                    )}
                    <label className="flex flex-col items-start gap-1">
                      <span className="text-[11px] font-semibold text-[#94a3b8] uppercase tracking-wide">Rows</span>
                      <input type="number" min={1} max={SHEET_MAX_ROWS} value={newSheetRows} autoFocus={cols.length > 0}
                        onChange={e => setNewSheetRows(e.target.value)} disabled={startingSheet}
                        className="w-[110px] px-2.5 py-1.5 border border-[#cbd5e1] rounded-md text-[13px] text-[#1e293b] outline-none focus:border-[#6366f1]"/>
                    </label>
                  </div>
                  <div className="text-[11px] text-[#94a3b8]">
                    {cols.length === 0 ? `Up to ${SHEET_MAX_COLS} columns and ${SHEET_MAX_ROWS} rows` : `Up to ${SHEET_MAX_ROWS} rows`}
                  </div>
                  <button type="submit" disabled={startingSheet || importing}
                    className="self-center px-5 py-1.5 rounded-md text-[13px] font-semibold cursor-pointer border-none text-white bg-[linear-gradient(135deg,#7c3aed,#6d28d9)] disabled:opacity-60 disabled:cursor-wait">
                    {startingSheet ? "Creating..." : "Create"}
                  </button>
                </form>

                <div className="flex items-center gap-2 my-4 text-[11px] font-semibold text-[#94a3b8]">
                  <div className="flex-1 h-px bg-[#e2e8f0]"/>OR<div className="flex-1 h-px bg-[#e2e8f0]"/>
                </div>

                <div className="flex flex-col gap-2 text-left">
                  <span className="text-[11px] font-semibold text-[#94a3b8] uppercase tracking-wide">Import file (CSV/XLSX)</span>
                  <div className="border border-dashed border-[#cbd5e1] rounded-md px-3 py-2.5 bg-[#f8fafc] flex items-center gap-2">
                    <input ref={importInputRef} type="file" accept=".csv,.xlsx" className="hidden"
                      onChange={e => { setImportFile(e.target.files?.[0] || null); e.target.value = ""; }}/>
                    <button type="button" onClick={() => importInputRef.current?.click()} disabled={importing || startingSheet}
                      className="px-3 py-1 rounded-md text-[12px] font-semibold cursor-pointer border border-[#cbd5e1] bg-white text-[#334155] hover:bg-[#f1f5f9] disabled:opacity-50">
                      Choose File
                    </button>
                    <span className="text-[12px] text-[#64748b] truncate flex-1" title={importFile?.name || ""}>
                      {importFile ? importFile.name : "No file chosen"}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#94a3b8] text-center">First row becomes the column names · max {SHEET_IMPORT_MAX_MB} MB</div>
                  <button type="button" onClick={importSheetFile} disabled={!importFile || importing || startingSheet}
                    className="self-center px-5 py-1.5 rounded-md text-[13px] font-semibold cursor-pointer border-none text-white bg-[linear-gradient(135deg,#7c3aed,#6d28d9)] disabled:opacity-50 disabled:cursor-not-allowed">
                    {importing ? "Importing..." : "Import"}
                  </button>
                </div>
                </>
              ) : (
                <div className="text-[12px] text-[#94a3b8]">Only members with edit access can add rows or columns.</div>
              )}
            </div>
          </div>,
          document.body
        )}
        <table ref={tableRef}
          className={`border-collapse text-[13px] table-fixed min-w-full select-none ${(tasks.length === 0 || cols.length === 0) ? "hidden" : ""}`}>
          {/* Row-number gutter sized to its digits; the trailing auto-width filler column takes
              the leftover width — otherwise table-fixed + min-w-full spread it over every
              column, and with few columns the gutter grew huge. */}
          <colgroup>
            <col style={{ width:gutterW, minWidth:gutterW, maxWidth:gutterW }}/>
            {cols.map((_,ci)=><col key={ci} style={{ width:colW[ci], minWidth:60 }}/>)}
            <col/>
          </colgroup>
          <thead>
            <tr>
              {/* Corner — click to select all */}
              <th onClick={selectAll}
                onContextMenu={e=>{
                  e.preventDefault();
                  selectAll();
                  if(!canWrite) return;
                  let pos={x:e.clientX,y:e.clientY};
                  if(pos.x+260>window.innerWidth) pos.x=window.innerWidth-264;
                  setCtx({ pos, items:[{ icon:"🗑️", label:"Delete All Rows & Columns", danger:true, onClick:deleteEntireSheet }] });
                }}
                className="sticky top-0 left-0 z-[7] border border-[#d0d7de] cursor-pointer text-center text-[11px] text-[#6c757d]"
                style={{ background: isAllSelected() ? "#b3d4f5" : "#f0f4f8" }}>
                <svg width="10" height="10" viewBox="0 0 10 10">
                  <polygon points="10,0 10,10 0,10" fill="#6c757d" opacity="0.4"/>
                </svg>
              </th>
              {cols.map((col,ci)=>{
                const bg=(ROW_COLORS[colBg[col]]||colBg[col])||"";
                const tx=(TEXT_COLORS_HEX[colTx[col]]||colTx[col])||"";
                const colSel = isColSelected(ci);
                return (
                  <th key={col} onContextMenu={e=>showColCtx(e,ci)}
                    onClick={e=>{
                      if(editCell) commitEdit();
                      select(0,ci,numRows-1,ci,0,ci);
                    }}
                    className={`sticky top-0 z-[6] border border-[#d0d7de] px-1.5 h-6 text-[11px] font-bold tracking-[0.04em] cursor-pointer align-middle whitespace-nowrap overflow-hidden${pendCls(colPendKind(ci))}`}
                    style={{
                      background: colSel ? "#b3d4f5" : (bg||"#f0f4f8"),
                      color: colSel ? "#0d47a1" : (tx||"#6c757d"),
                    }}>
                    <div className="flex items-center justify-between gap-0.5">
                      <span>{colLetter(ci)}</span>
                      <div onMouseDown={e=>startResize(e,ci)}
                        className="w-[5px] cursor-col-resize h-4 bg-transparent shrink-0"
                        onClick={e=>e.stopPropagation()}/>
                    </div>
                  </th>
                );
              })}
              <th aria-hidden="true" className="sticky top-0 z-[6] border-b border-[#d0d7de] bg-[#f0f4f8] p-0"/>
            </tr>
          </thead>
          <tbody>
            {[0, ...visibleDataRows].map(r=>(
              <tr key={r}>
                {/* Row header */}
                <td onContextMenu={r>0?e=>showRowCtx(e,r):undefined}
                  onClick={e=>{
                    if(editCell) commitEdit();
                    if(e.shiftKey && selStart) {
                      const minR=Math.min(selStart.r,r),maxR=Math.max(selStart.r,r);
                      setSelStart(p=>({...p,r:minR})); setSelEnd({r:maxR,c:numCols-1});
                    } else {
                      select(r,0,r,numCols-1);
                    }
                  }}
                  className={`sticky left-0 border border-[#d0d7de] text-center text-[11px] font-bold text-[#6c757d] cursor-pointer p-0 select-none overflow-visible${pendCls(rowPendKind(r))}`}
                  style={{
                    zIndex: (freezeTopRow&&r===0) ? 7 : 4,
                    top: (freezeTopRow && r===0) ? 24 : undefined,
                    background:isRowFullySelected(r) ? "#b3d4f5" : (r===0?"#fef9c3":"#f8fafc"),
                    color:isRowFullySelected(r) ? "#0d47a1" : undefined,
                    height:rowH[r]||26,
                  }}>
                  {r+1}
                  {r>0 && (
                    <div
                      onMouseDown={e => startRowResize(e, r, e.currentTarget.closest("tr"))}
                      onClick={e => e.stopPropagation()}
                      className="absolute bottom-0 left-0 right-0 h-1 cursor-row-resize bg-transparent z-10"/>
                  )}
                </td>
                {/* Data cells */}
                {cols.map((col,c)=>{
                  if (mergeCoveredSet.has(`${r}-${c}`)) return null;
                  const mergeOrigin = mergeOriginMap[`${r}-${c}`];
                  const colSpanVal = mergeOrigin ? (mergeOrigin.c2 - mergeOrigin.c1 + 1) : 1;
                  const rowSpanVal = mergeOrigin ? (mergeOrigin.r2 - mergeOrigin.r1 + 1) : 1;
                  const fmt = cellFormat[`${r}-${c}`] || {};
                  const isEdit = editCell?.r===r && editCell?.c===c;
                  const style  = getCellStyle(r,c);
                  const val    = grid[r]?.[c]??"";
                  const isDone = r>0 && rowComplete[r];
                  const isFillPreview = autoFillRange && r>=autoFillRange.r1 && r<=autoFillRange.r2 && c>=autoFillRange.c1 && c<=autoFillRange.c2;
                  const isSelEnd = selEnd && r===Math.max(selStart?.r??0,selEnd.r) && c===Math.max(selStart?.c??0,selEnd.c);
                  const isFrozenRow = freezeTopRow  && r === 0;
                  const isFrozenCol = freezeFirstCol && c === 0;
                  const freezeStyle = (isFrozenRow || isFrozenCol) ? {
                    position: "sticky",
                    ...(isFrozenRow ? { top: 24 } : {}),
                    ...(isFrozenCol ? { left: 36, background: style.background || "#fff" } : {}),
                    zIndex: isFrozenRow && isFrozenCol ? 6 : isFrozenRow ? 5 : 4,
                  } : {};
                  return (
                    <td key={c}
                      data-r={r} data-c={c}
                      colSpan={colSpanVal > 1 ? colSpanVal : undefined}
                      rowSpan={rowSpanVal > 1 ? rowSpanVal : undefined}
                      className={`h-[26px] p-0 overflow-hidden whitespace-nowrap relative max-w-0${cellPendCls(r,c)}`}
                      style={{ ...style,
                        border: isFillPreview ? "1px dashed #1f6feb" : "1px solid #d0d7de",
                        textAlign: mergeOrigin?.center ? "center" : style.textAlign,
                        zIndex: isEdit?5:isActive(r,c)?2:1,
                        ...freezeStyle }}
                      onMouseDown={e=>{
                        if(e.button!==0) return;
                        if(editCell) { commitEdit(); return; }
                        if(e.shiftKey && selStart) {
                          setSelEnd({r,c});
                        } else {
                          select(r,c,r,c);
                        }
                        isDragging.current=true;
                        e.preventDefault();
                      }}
                      onMouseEnter={()=>{
                        if(isDragging.current && selStart) setSelEnd({r,c});
                      }}
                      onMouseUp={()=>{ isDragging.current=false; }}
                      onDoubleClick={()=>startEdit(r,c)}
                      onContextMenu={e=>showCellCtx(e,r,c)}>
                      {isEdit ? (
                        <input ref={inputRef} value={editVal}
                          className="xl-edit-input w-full h-full border-none outline-none bg-transparent text-xs px-1.5 box-border"
                          style={{ minHeight:26, color:style.color }}
                          onChange={e=>setEditVal(e.target.value)}
                          onKeyDown={e=>{
                            if(e.key==="Enter"){e.preventDefault();commitEdit("down");}
                            else if(e.key==="Tab"){e.preventDefault();commitEdit(e.shiftKey?"left":"right");}
                            else if(e.key==="Escape"){cancelEdit();}
                          }}
                          onBlur={()=>commitEdit()}/>
                      ) : r===0 ? (
                        /* Header cell — show filter dropdown button + resize handle */
                        <>
                          <div className="flex items-center h-full pl-1.5">
                            <span title={String(val)}
                              className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-xs"
                              style={{ lineHeight:`${rowH[0]||26}px`, color:style.color||"#212529" }}>
                              {val}
                            </span>
                            <button
                              onMouseDown={e=>e.stopPropagation()}
                              onClick={e=>{
                                e.stopPropagation();
                                const rect=e.currentTarget.getBoundingClientRect();
                                setFilterDrop(fd=>fd?.ci===c?null:{ci:c,btnEl:e.currentTarget,pos:{x:rect.left,y:rect.bottom+2}});
                              }}
                              title="Filter"
                              className="shrink-0 w-4 h-[18px] flex items-center justify-center border-none rounded-sm cursor-pointer p-0 leading-none text-[7px]"
                              style={{
                                background: colFilters[c]||colSortDir[c] ? "#e8f0fe":"transparent",
                                color: colFilters[c]||colSortDir[c] ? "#1a73e8":"#94a3b8",
                              }}>
                              {colFilters[c] ? "⊛" : "▾"}
                            </button>
                          </div>
                          <div
                            onMouseDown={e=>{ e.stopPropagation(); startResize(e,c); }}
                            onClick={e=>e.stopPropagation()}
                            title="Drag to resize column"
                            className="absolute right-0 top-0 w-1.5 h-full cursor-col-resize z-20 bg-transparent"
                          />
                        </>
                      ) : (
                        <span title={String(val)}
                          className="flex items-center overflow-hidden text-ellipsis whitespace-nowrap"
                          style={{
                            justifyContent: fmt.align==="right" ? "flex-end" : fmt.align==="center" ? "center" : "flex-start",
                            paddingLeft: `${(fmt.indent||0)*16+6}px`,
                            paddingRight:"6px",
                            height:`${rowH[r]||26}px`,
                            color:isDone?"#adb5bd":style.color||"#212529",
                            textDecoration:[isDone?"line-through":"",fmt.underline?"underline":""].filter(Boolean).join(" ")||"none",
                          }}>
                          {val}
                        </span>
                      )}
                      {/* AutoFill handle */}
                      {isSelEnd && !editCell && canWrite && !autoFillDragging && (
                        <div
                          title="Drag to fill"
                          onMouseDown={e=>{
                            e.stopPropagation(); e.preventDefault();
                            autoFillDragRef.current = true;
                            autoFillEndRef.current  = { r, c };
                            setAutoFillDragging(true);
                            setAutoFillEnd({ r, c });
                          }}
                          className="absolute -bottom-[3px] -right-[3px] w-[7px] h-[7px] bg-[#1f6feb] border-[1.5px] border-white rounded-[1px] cursor-crosshair z-30"
                        />
                      )}
                    </td>
                  );
                })}
                <td aria-hidden="true" className={`border-b border-[#eef2f6] p-0${pendCls(rowPendKind(r))}`}
                  style={{ background: r===0 ? "#fef9c3" : undefined }}/>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bottom bar */}
      <div className="flex items-center justify-between px-3 py-1 border-t border-[#e2e8f0] bg-[#f8fafc] shrink-0 text-[10px] text-[#94a3b8]">
        <div className="flex items-center gap-2">
          <span>{tasks.length} row{tasks.length!==1?"s":""}</span>
          {sheetStatus && (
            <span role="status" aria-live="polite"
              className={`flex items-center gap-1.5 font-semibold ${sheetStatus.kind==="busy" ? "text-[#6d28d9]" : sheetStatus.kind==="ok" ? "text-[#15803d]" : "text-[#dc2626]"}`}>
              {sheetStatus.kind==="busy" && (
                <span className="inline-block w-2.5 h-2.5 rounded-full border-2 border-[#c4b5fd] border-t-[#6d28d9] animate-spin"/>
              )}
              {sheetStatus.text}
            </span>
          )}
          {hasActiveFilter && (
            <span className="flex items-center gap-[5px]">
              <span className="text-[#1a73e8] font-bold">
                ({visibleDataRows.length} shown)
              </span>
              <button onClick={clearAllFilters}
                className="text-[10px] px-1.5 py-px rounded-[3px] border border-[#c7d2fe] bg-[#e8f0fe] text-[#1a73e8] cursor-pointer font-bold">
                ✕ Clear Filters
              </button>
            </span>
          )}
        </div>
        <span>{cols.length} col{cols.length!==1?"s":""}</span>
      </div>

      {/* Find & Replace */}
      {findOpen && (
        <FindBar
          findVal={findVal}
          replaceVal={replaceVal}
          matchCase={matchCase}
          matchResults={matchResults}
          matchIdx={matchIdx}
          onFindChange={v => { setFindVal(v); setMatchIdx(0); }}
          onReplaceChange={setReplaceVal}
          onMatchCaseToggle={() => { setMatchCase(m => !m); setMatchIdx(0); }}
          onNext={findNext}
          onPrev={findPrev}
          onReplace={doReplace}
          onReplaceAll={doReplaceAll}
          onClose={() => { setFindOpen(false); setFindVal(""); setReplaceVal(""); }}
          canWrite={canWrite}
        />
      )}

      {/* Context Menu */}
      {ctx && <ContextMenu items={ctx.items} pos={ctx.pos} onClose={()=>setCtx(null)}/>}

      {/* Filter Dropdown */}
      {filterDrop && (
        <FilterDropdown
          ci={filterDrop.ci}
          pos={filterDrop.pos}
          btnEl={filterDrop.btnEl}
          uniqueVals={[...new Set(
            Array.from({length: tasks.length}, (_, i) => String(grid[i+1]?.[filterDrop.ci] ?? ""))
          )].sort((a,b) => a.localeCompare(b, undefined, {numeric:true, sensitivity:"base"}))}
          activeFilter={colFilters[filterDrop.ci] || null}
          activeSort={colSortDir[filterDrop.ci] || null}
          onApply={applyFilter}
          onClose={() => setFilterDrop(null)}
        />
      )}

      {/* Activity */}
      <ActivityOffcanvas groupId={groupId} open={activityOpen} onClose={()=>setActivityOpen(false)} clientLogs={clientLogs}/>

      {/* Members */}
      <MembersOffcanvas
        groupId={groupId} open={membersOpen} onClose={()=>setMembersOpen(false)}
        members={members} isAdmin={isAdmin} adminId={adminId} onReload={onMembershipChange}/>

      {/* ── Remove Duplicates: Step 1 — Warning dialog ── */}
      {removeDupStep===1 && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/[.35]">
          <div className="bg-white rounded-md shadow-[0_8px_32px_rgba(0,0,0,0.25)] px-6 py-5 min-w-[380px] max-w-[500px] font-['Segoe_UI',Arial,sans-serif]">
            {/* Title */}
            <div className="flex items-center justify-between mb-3.5">
              <span className="text-sm font-bold text-[#1e293b]">Remove Duplicates Warning</span>
              <div className="flex gap-2.5">
                <span className="text-[13px] text-[#94a3b8] cursor-default">?</span>
                <button onClick={()=>setRemoveDupStep(0)}
                  className="bg-transparent border-none cursor-pointer text-base text-[#94a3b8] leading-none">✕</button>
              </div>
            </div>
            {/* Message */}
            <div className="text-[13px] text-[#1e293b] mb-4 leading-[1.6]">
              Microsoft Excel found data next to your selection. Because you have not selected this data, it will not be removed.<br/><br/>
              <strong>What do you want to do?</strong>
            </div>
            {/* Radio options */}
            <div className="flex flex-col gap-2.5 mb-5">
              {[
                { val:"expand",  label:"Expand the selection" },
                { val:"current", label:"Continue with the current selection" },
              ].map(opt=>(
                <label key={opt.val} className="flex items-center gap-2 cursor-pointer text-[13px] text-[#1e293b]">
                  <input type="radio" name="dupWarn" value={opt.val}
                    checked={removeDupWarn===opt.val}
                    onChange={()=>setRemoveDupWarn(opt.val)}
                    className="w-[13px] h-[13px]" style={{ accentColor:"#3b82f6" }}/>
                  {opt.label}
                </label>
              ))}
            </div>
            {/* Buttons */}
            <div className="flex justify-end gap-2">
              <button onClick={()=>setRemoveDupStep(0)}
                className="px-[18px] py-[5px] text-[13px] border border-[#d0d7de] rounded bg-white cursor-pointer text-[#24292f]">
                Cancel
              </button>
              <button onClick={removeDupProceed}
                className="px-[18px] py-[5px] text-[13px] border border-[#d0d7de] rounded bg-[#f6f8fa] cursor-pointer text-[#24292f] font-semibold">
                Remove Duplicates...
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Remove Duplicates: Step 2 — Column picker ── */}
      {removeDupStep===2 && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/[.35]">
          <div className="bg-white rounded-md shadow-[0_8px_32px_rgba(0,0,0,0.25)] px-[22px] py-5 min-w-[360px] max-w-[460px] font-['Segoe_UI',Arial,sans-serif]">
            {/* Title */}
            <div className="flex items-center justify-between mb-3.5">
              <span className="text-sm font-bold text-[#1e293b]">Remove Duplicates</span>
              <div className="flex gap-2.5">
                <span className="text-[13px] text-[#94a3b8] cursor-default">?</span>
                <button onClick={()=>setRemoveDupStep(0)}
                  className="bg-transparent border-none cursor-pointer text-base text-[#94a3b8] leading-none">✕</button>
              </div>
            </div>
            {/* Description */}
            <div className="text-xs text-[#475569] mb-3 leading-[1.5]">
              To delete duplicate values, select one or more columns that contain duplicates.
            </div>
            {/* Select All / Unselect All + My data has headers */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex gap-1.5">
                <button onClick={()=>setRemoveDupCols([...removeDupDisplayCols])}
                  className="px-2.5 py-1 text-xs border border-[#d0d7de] rounded bg-[#f6f8fa] cursor-pointer flex items-center gap-1">
                  <span className="text-[13px]">☑</span> Select All
                </button>
                <button onClick={()=>setRemoveDupCols([])}
                  className="px-2.5 py-1 text-xs border border-[#d0d7de] rounded bg-[#f6f8fa] cursor-pointer flex items-center gap-1">
                  <span className="text-[13px]">☐</span> Unselect All
                </button>
              </div>
              <label className="flex items-center gap-[5px] text-xs cursor-pointer text-[#1e293b]">
                <input type="checkbox" checked={removeDupHeaders}
                  onChange={e=>setRemoveDupHeaders(e.target.checked)}
                  className="w-[13px] h-[13px]" style={{ accentColor:"#3b82f6" }}/>
                My data has headers
              </label>
            </div>
            {/* Column list */}
            <div className="border border-[#d0d7de] rounded overflow-hidden mb-4">
              <div className="bg-[#f6f8fa] border-b border-[#d0d7de] px-2.5 py-[5px] text-[11px] font-semibold text-[#57606a] tracking-[.4px]">
                Columns
              </div>
              <div className="max-h-[200px] overflow-y-auto">
                {removeDupDisplayCols.map(ci=>{
                  const headerVal = String(grid[0]?.[ci]??"").trim();
                  const checked = removeDupCols.includes(ci);
                  const colName = removeDupHeaders && headerVal ? headerVal : `Column ${colLetter(ci)}`;
                  return (
                    <label key={ci}
                      className={`flex items-center gap-2 px-2.5 py-1.5 cursor-pointer text-[13px] text-[#1e293b] border-b border-[#f0f0f0] ${checked ? "bg-[#e8f0fe]" : "bg-white"}`}>
                      <input type="checkbox" checked={checked}
                        onChange={()=>setRemoveDupCols(prev=>
                          prev.includes(ci) ? prev.filter(x=>x!==ci) : [...prev,ci])}
                        className="w-[13px] h-[13px]" style={{ accentColor:"#3b82f6" }}/>
                      <span>{colName}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            {/* Buttons */}
            <div className="flex justify-end gap-2">
              <button onClick={()=>setRemoveDupStep(0)}
                className="px-[18px] py-[5px] text-[13px] border border-[#d0d7de] rounded bg-white cursor-pointer text-[#24292f]">
                Cancel
              </button>
              <button onClick={performRemoveDuplicates}
                disabled={removeDupCols.length===0}
                className={`px-[18px] py-[5px] text-[13px] border-none rounded text-white font-semibold ${
                  removeDupCols.length ? "bg-[#0969da] cursor-pointer" : "bg-[#93c5fd] cursor-not-allowed"
                }`}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Remove Duplicates: Step 3 — Result dialog ── */}
      {removeDupStep===3 && removeDupResult && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50">
          <div className="bg-[#1e2d3d] rounded-lg shadow-[0_8px_32px_rgba(0,0,0,0.5)] px-8 py-7 min-w-[340px] max-w-[440px] font-['Segoe_UI',Arial,sans-serif] text-center relative">
            <button onClick={()=>setRemoveDupStep(0)}
              className="absolute top-2.5 right-3.5 bg-transparent border-none cursor-pointer text-lg text-[#94a3b8] leading-none">✕</button>
            {/* Icon + title */}
            <div className="flex items-center gap-2.5 justify-center mb-4">
              <span className="text-[28px] text-[#22d3ee]">ℹ</span>
              <span className="text-base font-bold text-[#22d3ee]">MyTask360 Work Group</span>
            </div>
            {/* Message */}
            <div className="text-[13px] text-[#e2e8f0] leading-[1.7] mb-[22px] text-left">
              {removeDupResult.removed > 0
                ? <>{removeDupResult.removed} duplicate value{removeDupResult.removed!==1?"s":""} found and removed; {removeDupResult.remaining} unique value{removeDupResult.remaining!==1?"s":""} remain.</>
                : <>No duplicate values were found.</>
              }{" "}
              Note that counts may include empty cells, spaces, etc.
            </div>
            {/* OK */}
            <div className="flex justify-center">
              <button onClick={()=>setRemoveDupStep(0)}
                className="px-7 py-1.5 text-[13px] border-2 border-[#22d3ee] rounded bg-transparent text-[#22d3ee] cursor-pointer font-semibold">
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onDone={()=>setToast(null)}/>}
    </div>
  );
}

/* ══ MAIN GroupDetails ══════════════════════════════════════ */
export default function GroupDetails({ group, onReload: reloadGroups }) {
  const [apiData,  setApiData]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  const fetchData = useCallback(async () => {
    if(!group?.id) return;
    setLoading(true); setError(null);
    try {
      const res = await axiosInstance.get(`/task_assignment/todo_group/tasks/${group.id}`);
      if(res.data?.success) setApiData(res.data);
      else setError(res.data?.error||"Failed to load");
    } catch { setError("Failed to load group data."); }
    finally { setLoading(false); }
  },[group?.id]);

  useEffect(() => { setApiData(null); fetchData(); },[fetchData]);

  // Stable fallbacks: a fresh []/{} each render would make Spreadsheet's sync effect
  // reset its local state (e.g. rows/columns just added to an empty sheet).
  const columns         = apiData?.columns             || EMPTY_ARR;
  const tasks           = apiData?.tasks               || EMPTY_ARR;
  const headerRow       = apiData?.header_row          || null;
  const colDisplayNames = apiData?.col_display_names   || EMPTY_OBJ;
  const colStyles       = apiData?.col_styles          || EMPTY_OBJ;
  const members         = apiData?.member_details      || group?.memberList || [];
  const canWrite        = apiData?.current_user_can_write ?? group?.canWrite ?? false;
  const isAdmin         = apiData?.current_user_is_admin  ?? group?.is_admin  ?? false;
  const adminId         = apiData?.admin_id || "";

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-white overflow-hidden">

      {/* Body */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {loading && (
          <div className="flex-1 flex flex-col items-center justify-center gap-3">
            <div className="w-8 h-8 border-4 border-[#ede9fe] rounded-full [animation:spin_0.8s_linear_infinite]" style={{ borderTopColor:"#7c3aed" }}/>
            <p className="text-[#94a3b8] text-[13px] m-0">Loading spreadsheet…</p>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        )}
        {!loading && error && (
          <div className="flex-1 flex flex-col items-center justify-center gap-3">
            <p className="text-[#dc2626] text-[13px] text-center m-0">{error}</p>
            <button onClick={fetchData}
              className="flex items-center gap-1.5 px-3.5 py-1.5 border-none rounded-lg bg-[linear-gradient(135deg,#7c3aed,#6d28d9)] text-white text-xs font-semibold cursor-pointer">
              <RefreshCw size={12}/> Retry
            </button>
          </div>
        )}
        {/* Rendered even with 0 columns: the sheet then shows its own "Add Rows / Add Columns" panel */}
        {!loading && !error && (columns.length>0 || apiData) && (
          <Spreadsheet
            groupId={group.id}
            groupName={group.name}
            groupColor={group.color}
            canWrite={canWrite}
            isAdmin={isAdmin}
            columns={columns}
            colDisplayNames={colDisplayNames}
            colStyles={colStyles}
            headerRow={headerRow}
            tasks={tasks}
            members={members}
            adminId={adminId}
            onReload={fetchData}
            onMembershipChange={() => { fetchData(); reloadGroups?.(); }}
          />
        )}
      </div>
    </div>
  );
}